/**
 * GuruOne — Strategy Canvas Section Guidance
 * ─────────────────────────────────────────────────────────────────
 * Single source of truth for how GuruOne guides founders through
 * each Strategy Canvas section. Each entry captures the objective,
 * what good looks like, the probing pattern for the conversation,
 * the output structure, and the option paradigm used when proposing
 * choices to the founder.
 *
 * Consumed by:
 *   - The Explore conversation prompt builder (backend chat endpoint)
 *   - The option generator (backend)
 *   - Optional UI surfacing (section cards / explore intro)
 *
 * Companion document: /docs/SECTION_GUIDANCE_SPEC.md (the prose
 * version, which is the reasoning record and the easier surface for
 * humans to review and edit).
 *
 * Schema for each entry:
 *   objective                  {string}  one sentence
 *   why_it_matters             {string}  why bother; downstream use
 *   good_answer_looks_like     {string}  qualities of a strong answer
 *   probe_deeper_on            {string}  surface patterns to probe past
 *   conversation_archetype     {string}  shape of questions to ask
 *   output_structure           {string}  per-field | grouped | per-entry
 *   option_paradigm            {string}  what "options" means here
 *   field_notes                {object}  per-field notes & overrides
 *   archetypes                 {array}   option archetypes (for grouped
 *                                        sections) — each = coherent
 *                                        combination across fields
 *   narrative_output           {object}  prose summary generated at end of
 *                                        conversation — audience, length,
 *                                        voice. Null for structural sections.
 *   cross_reference            {object}  { up, down } coherence network
 *                                        of sections this one should be
 *                                        tested against for consistency.
 *                                        Optionally also carries:
 *                                          coherence_scope — when present
 *                                            (e.g. 'per_entry_against_full_canvas'),
 *                                            signals that coherence is
 *                                            checked at the per-entry
 *                                            content level against the
 *                                            full canvas rather than via
 *                                            enumerated section up-refs.
 *                                            Used by per-entry sections
 *                                            like Current Challenges
 *                                            where any entry may tension
 *                                            with any upstream section.
 *   decision_guru_triggers     {array}   journeys this section can hand
 *                                        the founder over to. Each entry:
 *                                          prompt               — founder-voice challenge (human readable)
 *                                          prompt_template      — challenge text pre-populated into DG
 *                                                                  entry, with {placeholders} for canvas
 *                                                                  field values. Optional.
 *                                          per_entry            — boolean. When true, the trigger is
 *                                                                  attached to each captured entry
 *                                                                  (for per-entry sections) rather than
 *                                                                  the section as a whole. Default: false.
 *                                          lens                 — 'market' | 'team' | 'profit' — the
 *                                                                  lens the CONTEXTUALLY-CHARGED TRIAGE
 *                                                                  resolves to, given the triggering
 *                                                                  conditions + canvas state. The existing
 *                                                                  triage engine is still invoked — it just
 *                                                                  runs with richer input than a bare
 *                                                                  founder-typed challenge. Surfaced
 *                                                                  transparently to the founder on arrival
 *                                                                  in DG with the reasoning, editable if
 *                                                                  they disagree. Optional.
 *                                                                  May also be an object:
 *                                                                    { from_field: '<field>', mapping: { <value>: <lens>, ... } }
 *                                                                  — derives the lens from the entry's
 *                                                                  field value (used in per-entry sections
 *                                                                  where lens varies by entry content).
 *                                          triggering_conditions— structured conditions that both (a) cause
 *                                                                  the offer to surface in the section, and
 *                                                                  (b) feed into triage as the context
 *                                                                  charge — cross-ref tensions, field
 *                                                                  patterns, founder statements. Optional.
 *                                          when                 — narrative description of when to offer
 *                                          updates_field        — canvas field this journey writes back to
 *   user_facing_intro          {object}  three short blocks shown to the
 *                                        founder before/during Explore:
 *                                        what, why, strong_articulation.
 *                                        Second-person voice, not the
 *                                        internal spec language.
 */

const SECTION_GUIDANCE = {

  // ── Foundation group ──────────────────────────────────────────

  founder_purpose: {
    objective:
      "To surface the personal driver and boundary conditions the founder " +
      "brings to the business — the 'why me, why this, for how long, under " +
      "what conditions' that shapes every strategic choice downstream.",

    why_it_matters:
      "Every later decision — growth pace, who to hire, whether to raise " +
      "money, when to exit — is filtered through these personal factors, " +
      "whether consciously or not. Making them explicit means the business " +
      "can be designed around them rather than drift away from them. " +
      "Misaligned founders drift into businesses they resent.",

    good_answer_looks_like:
      "A specific origin story rather than a generic opportunity narrative. " +
      "A primary motivation the founder can defend with examples, not one " +
      "they think sounds admirable. Risk tolerance grounded in actual " +
      "behaviour — risks taken and risks avoided — not self-description. " +
      "A time horizon that reflects their life, not a default 'long-term'.",

    probe_deeper_on:
      "Founders often start with surface-level answers — not because they " +
      "are wrong, but because they have not yet had the prompt to go " +
      "deeper. Watch for: generic origin stories ('I spotted a gap'), " +
      "self-descriptions of motivation rather than behavioural evidence " +
      "('I want to make a difference'), risk self-ratings without concrete " +
      "examples, and time horizons that default to 'long-term' because " +
      "that is what founders think they should say. In each case, probe " +
      "with specifics and real examples — the founder finds their own " +
      "answer, the AI helps them see it. Never judgemental.",

    conversation_archetype:
      "Start with the origin — what specifically happened that made this " +
      "business exist? Force concreteness. Probe motivation by asking about " +
      "trade-off scenarios ('if you could have £5m wealth OR your industry " +
      "transformed, which?'). Probe risk tolerance by asking about actual " +
      "decisions taken or avoided ('What's the biggest financial risk " +
      "you've taken since starting the business? What's a move you've " +
      "considered but held back from?'). Probe time horizon by asking what " +
      "their life looks like in 5 years — does this business still occupy " +
      "it? Challenge generic answers with 'say more' and 'what's an example'.",

    output_structure: 'grouped', // one conversation, integrated framings

    option_paradigm:
      "Three framings that represent different plausible interpretations " +
      "of the same founder, not three random permutations. Each framing is " +
      "a coherent combination of all four dimensions. This is a mirror " +
      "exercise, not a generation exercise — the AI helps the founder " +
      "recognise themselves, not invent them.",

    field_notes: {
      primary_motivation:
        "Change from single-value to primary + secondary pair. Decision-" +
        "making force comes from knowing which wins in conflict; a four-" +
        "way slider just diffuses that.",
      risk_tolerance:
        "Keep Low / Moderate / High chip as the at-a-glance summary. Add a " +
        "short text extension for 'risks you've taken / risks you won't take'. " +
        "Text is what the AI uses for downstream reasoning; chip is the " +
        "visual shorthand.",
      time_horizon:
        "Rename 'Build long-term' to 'Run indefinitely'. Cleaner and more " +
        "definitive. Keep 'Unsure' option — it's honest for early-stage " +
        "founders; the AI probes it rather than accepting it wholesale.",
    },

    // Archetype candidates. The AI shouldn't feel bound to these three — it
    // should generate framings grounded in the actual conversation. These
    // are examples of the *shape* a coherent framing takes, not a fixed
    // library to pick from.
    archetypes: [
      {
        label: 'The reluctant entrepreneur',
        summary:
          "Started because they saw something broken and nobody else was " +
          "fixing it.",
        dimensions: {
          origin_driver: 'Problem I experienced',
          primary_motivation: 'Impact',
          risk_tolerance: 'Moderate',
          time_horizon: 'Run indefinitely',
        },
      },
      {
        label: 'The industry builder',
        summary: "Sees a structural shift and wants to lead it.",
        dimensions: {
          origin_driver: 'Opportunity spotted',
          primary_motivation: 'Legacy',
          risk_tolerance: 'High',
          time_horizon: 'Run indefinitely',
        },
      },
      {
        label: 'The craftsman',
        summary:
          "Loves the work itself; the business is a vehicle for practising " +
          "a craft at scale, not a vehicle for exit.",
        dimensions: {
          origin_driver: 'Passion for craft',
          primary_motivation: 'Lifestyle',
          risk_tolerance: 'Low',
          time_horizon: 'Run indefinitely',
        },
      },
    ],

    narrative_output: {
      audience: 'The founder themselves, and optionally team leadership when shared',
      length_target: '3–5 sentences, first-person voice',
      purpose:
        "Personal grounding. A short reflection the founder revisits when " +
        "strategic pressure builds, to check whether the business is still " +
        "serving their actual purpose. Not for marketing copy. Derived from " +
        "the origin story, motivation, and chosen archetype.",
      editable: true,
    },

    decision_guru_triggers: [],
    // Foundational-state section. Founder Purpose captures personal
    // driver and boundaries — life decisions rather than strategic
    // business journeys. The data it captures feeds other sections'
    // triggers (e.g. time_horizon feeds revenue_model and
    // go_to_market triggers) rather than originating journeys here.

    user_facing_intro: {
      what:
        "This is about you — what drew you to this business, what you want " +
        "from it, and the personal boundaries within which you want to run " +
        "it. Everything else in the canvas is built on top of this.",
      why:
        "Every strategic decision you'll make — how fast to grow, who to " +
        "hire, whether to raise money, when to step back — runs through " +
        "these personal factors whether you name them or not. Making them " +
        "explicit means the business gets shaped around what you actually " +
        "want, instead of drifting somewhere you didn't choose.",
      strong_articulation:
        "A specific origin story, not a generic one. A primary motivation " +
        "you can defend with examples — what you've actually done, not " +
        "what sounds admirable. A risk appetite grounded in real " +
        "decisions. A time horizon that matches your life, not one " +
        "borrowed from a business plan template.",
    },

    cross_reference: {
      up: [],   // foundation section — nothing upstream
      down: [
        {
          section: 'values',
          checks: ['values coherent with stated motivations and risk tolerance'],
        },
        {
          section: 'mission',
          checks: ['mission coherent with primary motivation and time horizon'],
        },
        {
          section: 'vision',
          checks: [
            'vision ambition coherent with risk tolerance',
            'vision time horizon within founder personal planning window',
            'what vision success looks like coherent with primary motivation',
          ],
        },
        {
          section: 'value_proposition',
          checks: [
            'market positioning coherent with risk tolerance (Best-in-class demands high; Reliable & efficient typically needs scale commitment)',
            'value prop coherent with primary motivation',
          ],
        },
        {
          section: 'go_to_market',
          checks: [
            'GTM aggressiveness coherent with stated risk tolerance',
            'retention investment coherent with time horizon (a 3-year exit makes retention investment harder to justify than indefinite)',
          ],
        },
        {
          section: 'revenue_model',
          checks: [
            'time_horizon coherent with revenue blend — long horizon supports subscription investment; short horizon favours project/transaction cash',
          ],
        },
        {
          section: 'cost_model',
          checks: [
            'risk_tolerance coherent with fixed_variable_ratio — Low risk tolerance + Mostly fixed is a real tension',
          ],
        },
      ],
    },
  },

  // values, mission, vision, value_proposition, customers, competitors,
  // differentiators, go_to_market, revenue_model, cost_model,
  // current_challenges — all defined below. Canvas ends at Current
  // Challenges; prescriptive work handed to Design Guru downstream.

  values: {
    objective:
      "To identify the small number of principles the organisation " +
      "genuinely stands by — the lines it won't cross even under " +
      "commercial pressure — and the way those principles show up in " +
      "day-to-day behaviour.",

    why_it_matters:
      "Values should drive behaviour. When they are genuine and visible — " +
      "on the wall, in the way people talk, in the decisions leaders " +
      "make — they are a positive reinforcement of expected behaviour. " +
      "Articulated clearly, they make hiring easier (test candidates " +
      "against them), protect culture during scale (new joiners inherit " +
      "them), and give leaders a language for saying no. The failure mode " +
      "is values that don't reflect how the business actually behaves — " +
      "put those on the wall and the gap between claim and reality " +
      "becomes visible daily, which erodes trust faster than having no " +
      "stated values at all.",

    good_answer_looks_like:
      "Up to 5 principles (more than that loses force). Phrased in a way " +
      "that's distinctive to this business, not generic — 'Customer focus' " +
      "reads very differently at a design agency vs a care home. " +
      "Evidenced in actual behaviour, not just aspiration. Paired with " +
      "concrete non-negotiables — 'here's what we'd never do, and here's " +
      "an example of it being tested' — because that's where values " +
      "become operational.",

    probe_deeper_on:
      "The instinct to pick the familiar list (Integrity, Excellence, " +
      "Collaboration) because they sound right. The comfortable answers " +
      "aren't wrong, but they're often inherited from corporate culture " +
      "rather than chosen deliberately. Also watch for values that sound " +
      "universal ('honesty') where the founder means something more " +
      "specific ('we never hide bad news from clients, even when it costs " +
      "us'). The specificity is the value — the abstract word is the " +
      "label. Probe with: 'tell me about a time that value cost you " +
      "something' — if they can't, the value may be aspirational rather " +
      "than real. That's worth naming openly, not hiding.",

    conversation_archetype:
      "Start with behaviour, not labels. 'What's something this business " +
      "does — or refuses to do — that a competitor might not?' 'Tell me " +
      "about a time you walked away from work or a hire because of how " +
      "they behaved.' 'What behaviour in an employee would make you most " +
      "proud? Most disappointed?' Let the values emerge from stories, " +
      "then help the founder see the pattern. Only then propose candidate " +
      "labels. Close by probing non-negotiables — where would you actually " +
      "hold the line?",

    output_structure: 'per_entry_with_continuity',

    option_paradigm:
      "After each exploratory segment, AI proposes 3 candidate labels from " +
      "the chip list that best match the behaviour described, plus a " +
      "fourth 'something else' option that prompts the founder to describe " +
      "their own value. When they do, the AI gently probes for " +
      "behavioural expression ('what does that look like in practice " +
      "here?') — this is part of the value-creation debate, not a " +
      "gatekeeper. Custom values get added to the chip list for this " +
      "session. Founder picks one; it locks in. Conversation transitions " +
      "with awareness of what's already chosen — 'You've locked in " +
      "Integrity. What sits alongside it — a complement, or a tension you " +
      "want to name?'",

    field_notes: {
      core_themes:
        "RESTRUCTURE from string to { theme, descriptor } pairs. Each " +
        "value is a heading (1-2 words, e.g. 'Integrity') plus a one-" +
        "sentence descriptor of how that value shows up in the business " +
        "('we never hide difficult truths from clients, even when it " +
        "costs us the relationship'). The descriptor makes the value " +
        "usable downstream — without it, 'Integrity' is a word with no " +
        "shared meaning. AI generates the descriptor from the conversation " +
        "segment that produced that value; founder can edit. Custom " +
        "values follow the same two-field shape. Raise max from 3 to 5. " +
        "More than 5 loses force.",
      behavioural_translation:
        "Change to multi-select. A business might both document values and " +
        "use them in hiring.",
      decision_influence:
        "Keep as single-choice chip but treat as a weaker data point. It's " +
        "a self-report; AI should not weight it heavily in downstream " +
        "reasoning. Useful as a founder self-check ('does this rating " +
        "match the stories you just told me?') but not a primary input.",
      non_negotiables:
        "Restructure from single text field to repeatable entries. Each " +
        "entry: principle (short statement) + example (real moment it was " +
        "tested). This is where values become operational — worth the " +
        "structure.",
    },

    custom_values_allowed: true,
    custom_value_behavioural_probe:
      "When a founder adds a custom value, gently probe for its behavioural " +
      "expression: 'what does that look like in practice here?' Part of " +
      "the value-creation debate, not a gatekeeper.",

    narrative_output: {
      audience: 'Team members (onboarding, performance conversations), prospective hires (recruitment materials), and the founder as a reference point',
      length_target: '2–4 sentences',
      purpose:
        "A short passage describing how this business's values show up in " +
        "practice. Written in concrete behavioural language, not abstract. " +
        "Draws on the value descriptors and non-negotiable examples " +
        "captured in the structured fields.",
      editable: true,
    },

    decision_guru_triggers: [],
    // Foundational-state section. Values are an articulation of what
    // the business actually is, not a strategic choice. The data feeds
    // coherence checks on other sections (e.g. market_positioning
    // compatibility) rather than originating journeys here.

    user_facing_intro: {
      what:
        "The small number of principles this business actually stands by — " +
        "the lines it won't cross even under commercial pressure — and the " +
        "way those principles show up in day-to-day behaviour.",
      why:
        "Real values make hiring easier, protect culture as you grow, and " +
        "give you a language for saying no. When they genuinely match how " +
        "the business behaves, visibility reinforces them. When they don't, " +
        "putting them on the wall just makes the gap more obvious and " +
        "erodes trust faster than having no values at all.",
      strong_articulation:
        "Up to five principles, each phrased in language distinctive to " +
        "this business rather than generic. Each one evidenced in real " +
        "behaviour, not just aspiration. Paired with concrete " +
        "non-negotiables — what you'd never do, and an example of the " +
        "moment it was tested.",
    },

    cross_reference: {
      up: [
        {
          section: 'founder_purpose',
          checks: [
            'values coherent with stated motivations',
            'values the founder would hold under pressure given their risk tolerance',
          ],
        },
      ],
      down: [
        {
          section: 'mission',
          checks: ['mission does not contradict stated values'],
        },
        {
          section: 'vision',
          checks: ['vision does not require compromising a stated value'],
        },
        {
          section: 'value_proposition',
          checks: ['chosen market positioning aligns with stated values (e.g. "craft" tensions with Reliable & efficient; "accessibility" tensions with Best-in-class)'],
        },
      ],
    },
  },

  mission: {
    objective:
      "To articulate the organisation's contribution beyond profit — the " +
      "cause the business is in service of, that motivates the team and " +
      "earns the trust of customers and wider stakeholders.",

    why_it_matters:
      "A clear mission makes hiring easier, customer messaging more " +
      "honest, and trade-off decisions faster. Without one, every hard " +
      "call becomes a fresh argument with no reference point. It also " +
      "shapes what the business is willing to say no to — a business with " +
      "no mission beyond profit will chase anything that pays, which over " +
      "time erodes the identity customers and staff were drawn to in the " +
      "first place.",

    good_answer_looks_like:
      "Specific (not 'make the world better'). Active — describes what " +
      "the business does for its beneficiaries, not what it aspires to. " +
      "Credible — consistent with the business's actual operations and " +
      "current capabilities. References an external audience — the " +
      "mission should point outside the business's commercial interest, " +
      "to customers, the community, a profession, a cause, or another " +
      "group the business serves. Bigger than the founder's personal " +
      "goals.",

    probe_deeper_on:
      "Corporate aspirations dressed up as mission ('to be the best', " +
      "'to be the leader in X') — these are commercial goals, not " +
      "contributions. Generic statements that could apply to any business " +
      "in the sector ('to help small businesses grow') — probe for what's " +
      "different about this business's contribution. Mission statements " +
      "that collapse into the product description ('we make great X') — " +
      "mission lives above product, not inside it. Missions that only " +
      "reference internal audiences — ask about customers, the community, " +
      "or the wider group the business serves. Use the 5-whys technique " +
      "where helpful: keep asking 'why is that important?' until the " +
      "founder lands on something deeper than the surface answer. Often " +
      "the real mission is 2–3 levels below where they start.",

    conversation_archetype:
      "Start with beneficiaries: who actually benefits when this business " +
      "does its job well? Force enumeration — not just 'customers', but " +
      "specifically which customers, and also the team, suppliers, the " +
      "community, the industry. Then probe what changes for each. Use " +
      "the 5-whys pattern: when a founder says 'we help SME leaders', " +
      "follow with 'why is it important to you that they benefit?' and " +
      "keep going until something deeper surfaces. The goal is depth, " +
      "not interrogation — stop when the founder lands on something that " +
      "feels true to them.",

    worked_example:
      "GuruOne: Who actually benefits when this business does its job " +
      "well?\n" +
      "Founder: SME leaders.\n" +
      "GuruOne: And why is it important to you that they benefit?\n" +
      "Founder: They drive the economy and provide jobs.\n" +
      "GuruOne: That's true of SME leaders as a category generally — is " +
      "that the mission at its core, or is it deeper than that for you?\n" +
      "Founder: We want to ensure Scotland creates great jobs for our " +
      "kids.\n\n" +
      "The surface answer would have produced a generic mission. The " +
      "real mission surfaces two levels down and is specific, personal, " +
      "credible, and bigger than the founder.",

    output_structure: 'grouped',

    option_paradigm:
      "Three different framings of the same mission — each a coherent " +
      "interpretation of what the business is in service of. Framings " +
      "differ in emphasis, not in mutual exclusion. The AI should " +
      "generate framings grounded in the actual conversation rather than " +
      "picking from a fixed library.",

    archetypes: [
      {
        label: 'Customer-outcome-framed',
        summary:
          "Mission defined by what changes for customers.",
      },
      {
        label: 'Industry-change-framed',
        summary:
          "Mission defined by the shift the business wants to see in " +
          "its sector.",
      },
      {
        label: 'Community-framed',
        summary:
          "Mission defined by contribution to a wider group — local, " +
          "professional, societal, or generational.",
      },
      {
        label: 'Craft-led',
        summary:
          "Mission defined by the standard the business holds itself to " +
          "in its work, independent of who benefits.",
      },
    ],

    field_notes: {
      primary_beneficiary:
        "Keep free text. Add AI-driven probing for specificity during " +
        "the conversation ('how do they differ from other SMEs?'). Don't " +
        "add UI complexity.",
      core_objective_themes:
        "Keep multi-select chip list. Add a soft cap of 3 themes max — " +
        "same logic as values, too many dilutes mission.",
      success_definition:
        "MOVED TO VISION. Doesn't belong here; Vision is about what " +
        "success looks like, Mission is about what you contribute.",
      time_horizon:
        "REMOVED FROM MISSION. Redundant with Vision. Mission doesn't " +
        "expire on a 3-year cycle.",
    },

    narrative_output: {
      audience: 'Customers, team, partners, stakeholders — anyone asking "what is this business for?"',
      length_target: '1–2 sentences, external-audience-friendly',
      purpose:
        "The mission statement itself as copyable prose. The 'about us' " +
        "paragraph the founder should be able to copy directly into a " +
        "website, pitch deck, or brief. External-audience-friendly " +
        "(Mission is customer-facing, unlike Vision which is team-facing). " +
        "Derived from the chosen framing, beneficiaries, and objective " +
        "themes.",
      editable: true,
    },

    decision_guru_triggers: [],
    // Foundational-state section. Mission is usually fixed once
    // articulated — the "what is this business for" answer. Rare to
    // revisit as a decision journey; when it does shift, that's
    // typically the OUTPUT of other strategic pivots, not a journey
    // originating from this section.

    user_facing_intro: {
      what:
        "The contribution your business makes beyond making money — the " +
        "cause it serves, stated in a way that motivates your team and " +
        "earns the trust of customers and wider stakeholders.",
      why:
        "A clear mission makes hiring easier, messaging more honest, and " +
        "trade-off decisions faster. Without one, every hard call becomes " +
        "a fresh argument with no reference point. It also shapes what the " +
        "business is willing to say no to — which is where identity gets " +
        "protected over time.",
      strong_articulation:
        "Specific, not 'make the world better'. Active — describes what " +
        "the business does for the people it serves, not what it hopes to " +
        "do. Credible given how the business actually operates today. " +
        "Points at someone outside your commercial interest — a customer " +
        "group, a community, a cause, a profession. Bigger than your " +
        "personal goals.",
    },

    cross_reference: {
      up: [
        {
          section: 'founder_purpose',
          checks: [
            'mission coherent with founder primary motivation',
            'mission scope consistent with founder time horizon',
          ],
        },
        {
          section: 'values',
          checks: [
            'mission does not contradict stated values',
          ],
        },
      ],
      down: [
        {
          section: 'vision',
          checks: ['vision expresses mission reaching maturity, not a disconnected commercial plan'],
        },
        {
          section: 'value_proposition',
          checks: ['customer and problem coherent with mission beneficiary'],
        },
        {
          section: 'customers',
          checks: ['prioritised segments coherent with mission beneficiary'],
        },
        {
          section: 'competitors',
          checks: ['competitors are those fighting for the mission beneficiary\'s attention, not rivals from a different market'],
        },
        {
          section: 'differentiators',
          checks: ['differentiators serve what the mission says the business is for, not a disconnected commercial narrative'],
        },
      ],
    },
  },

  vision: {
    objective:
      "To make concrete the destination the business is heading toward — " +
      "where it will be, by when, and what will be true that isn't true " +
      "today. Vision translates mission (the ongoing cause) into a picture " +
      "of a specific future state the business is working toward. Vision " +
      "is primarily for the team who will build it, and secondarily for " +
      "investors and commercially incentivised stakeholders — not for " +
      "customers. Customer-facing articulation is Mission's job.",

    why_it_matters:
      "Mission gives you a direction; vision gives you a finish line (even " +
      "if it's provisional). Without a vision, strategy has no anchor — " +
      "teams can't tell whether a given move is taking them toward or " +
      "away from where they're trying to get to. Vision also forces honest " +
      "pricing of ambition: a founder who claims 'mission-first' but has " +
      "no concrete vision is often protecting themselves from having to " +
      "declare what success would actually look like. The vision is also " +
      "the primary internal hiring and retention tool — people choose to " +
      "join and stay based on whether they want to be part of what's " +
      "being built.",

    good_answer_looks_like:
      "Concrete — '£2m ARR across 3 locations' beats 'grow significantly'. " +
      "Time-bound — a horizon the founder can hold in their head. " +
      "Consistent with Mission — the vision should look like an " +
      "expression of the mission reaching maturity. Stretching but " +
      "plausible — a vision the founder finds achievable without effort " +
      "isn't a vision; one they secretly know is impossible is just a " +
      "wish. Written in a voice the team can recognise — concrete enough " +
      "that someone could tell whether they'd want to be part of getting " +
      "there.",

    probe_deeper_on:
      "Visions that restate current operations at larger scale ('5x the " +
      "revenue, same model') — ask what has to be different structurally " +
      "about the business to get there. Horizons that extend beyond the " +
      "founder's own personal planning window. Commercial visions " +
      "disconnected from Mission. Visions built around exit valuations " +
      "with no description of what the business does at that point — " +
      "exit is an outcome, not a destination.",

    conversation_archetype:
      "Anchor on time horizon first — how far out does the business leader " +
      "want to look? Accept either a duration or a target year (some plan " +
      "to a specific year like '2030 strategy', others think in " +
      "durations). Then ask for the concrete picture: describe the " +
      "business as it exists on that day — what does it do, how big is " +
      "it, who does it serve, what's changed from today? Press for " +
      "specifics. Cross-check against Mission: does this version of the " +
      "business still serve the mission? Probe success definition — " +
      "what's the headline metric or outcome that tells the leader this " +
      "vision has been achieved? Finally, sense-check voice and audience: " +
      "would this vision make sense to a prospective senior hire reading " +
      "it? If it sounds like marketing copy or a pitch deck, it's pointed " +
      "at the wrong audience.",

    // ── Stage-aware vision ─────────────────────────────────────────
    // A good vision does two jobs simultaneously: paints an aspirational
    // long-term picture, and lays a credible near-term path to it. The
    // weighting between these shifts by stage. The Explore dialogue uses
    // this to calibrate what it pushes for; the evaluator uses the
    // matching criteria block on the backend.
    stage_chip_help:
      "Vision needs to do two jobs at once — paint an aspirational " +
      "destination AND show a credible near-term path. The weighting " +
      "between them depends on where the business is right now. Pick " +
      "the closest match so the dialogue and the review calibrate to " +
      "your stage.",

    business_stage_question:
      "Before we get into the vision itself — where would you say the " +
      "business is right now? Early days and still finding your footing, " +
      "actively scaling a model that's working, or more established and " +
      "stable?",

    stage_guidance: {
      early:
        "STAGE = EARLY (pre-revenue or early traction).\n" +
        "Push hard on the 10–15 year ambition. The aspirational picture " +
        "is doing the heavy lifting at this stage — a modest vision " +
        "signals a small TAM and kills ambition. Challenge anything " +
        "that feels incremental or describes 'the current business but " +
        "bigger'. Ask: 'If this fully worked, what does the world look " +
        "like that doesn't today?' Don't over-probe near-term " +
        "milestones — ask only for the first meaningful step (the " +
        "first £X of revenue, the first 10 customers, the first " +
        "validated wedge). Most of the rigour belongs in the long view.",
      growth:
        "STAGE = GROWTH (scaling a model that's working).\n" +
        "Balance both dimensions equally. The long-term ambition must " +
        "still hold up — probe whether it has staying power and whether " +
        "the leader still believes in it. Then probe the 3–5 year view " +
        "for specificity: what does success look like in that window, " +
        "what milestones evidence it, and how will the leader know if " +
        "they're on or off track? Both halves carry equal weight here.",
      established:
        "STAGE = ESTABLISHED (mature, stable business).\n" +
        "Anchor on the 3–5 year view as the primary output. Aspiration " +
        "should still be present but grounded — vision now functions " +
        "as strategic direction and culture anchor, not investor pitch. " +
        "Push back hard if the vision is vague or sounds like marketing " +
        "copy without substance. Probe specificity, credibility, and " +
        "alignment with current capabilities. Overly aspirational " +
        "language at this stage reads as unfocused, not ambitious.",
    },

    output_structure: 'grouped',

    option_paradigm:
      "Three different framings of the vision — each an integrated, " +
      "plausible interpretation of what the founder has described in the " +
      "conversation. Framings differ in structural emphasis (growth " +
      "shape, headline metric, exit posture), not in ambition level. The " +
      "founder picks the framing that lands truest. This mirrors the " +
      "Founder Purpose and Mission paradigm — a reflection exercise, not " +
      "a generation exercise. Once a framing is chosen, the AI proposes " +
      "2–3 draft vision_statement narratives in the founder's voice, " +
      "written for a team member or prospective hire (not a customer or " +
      "marketing audience); founder picks or edits.",

    field_notes: {
      target_type:
        "Keep single-choice as primary framing anchor. Secondary " +
        "dimensions can appear in target_detail.",
      time_horizon:
        "Support both duration and target-year inputs. Internally store " +
        "both when available. Chip list of common durations (1/3/5/10 " +
        "years) plus a 'By year…' option for specific strategy target " +
        "years (e.g. 2030, 2035).",
      vision_statement:
        "AI proposes 2–3 drafts in the founder's voice after framing is " +
        "chosen, written for a team-facing audience. Founder picks or " +
        "edits.",
      success_definition:
        "Inherited from Mission. Values: Revenue / Impact / Market share " +
        "/ Other. Frame as 'what tells you the vision has been achieved?'",
    },

    narrative_output: {
      audience: 'The team first, investors and commercial stakeholders second. NOT customers.',
      length_target: '3–5 sentences',
      purpose:
        "Write ONE first-person vision narrative in the leader's voice. " +
        "Written so a prospective senior hire could read it and decide " +
        "whether they want to be part of what's being built. Single piece " +
        "of prose — do NOT offer multiple alternatives or options for the " +
        "leader to pick from. The leader can edit it directly in the " +
        "'Finalise the one-line vision' field below.",
      maps_to_field: 'vision_statement',
      editable: true,
    },

    decision_guru_triggers: [],
    // Foundational-state section. Vision is revisable (more so than
    // Mission), but "should we reset our vision?" rarely arrives as a
    // standalone decision — it surfaces as the output of major GTM,
    // revenue, or capacity shifts captured elsewhere. No originating
    // trigger here for now; revisit after tester usage if founders
    // are reaching for this as a standalone journey.

    user_facing_intro: {
      what:
        "The destination your business is heading toward — where it will " +
        "be, by when, and what will be true that isn't true today. If " +
        "mission is the direction, vision is the finish line.",
      why:
        "Without a vision, strategy has no anchor — your team can't tell " +
        "whether a move is taking you toward or away from where you're " +
        "going. It's also your primary tool for attracting and keeping " +
        "talent: people choose to join and stay based on whether they " +
        "want to be part of what you're building.",
      strong_articulation:
        "Concrete — '£2m ARR across three locations' beats 'grow " +
        "significantly'. Time-bound, in a horizon you can actually hold " +
        "in your head. An expression of your mission reaching maturity, " +
        "not a commercial plan bolted on. Stretching but plausible. " +
        "Written in a voice your team recognises — specific enough that " +
        "someone could tell whether they'd want to be part of getting " +
        "there.",
    },

    cross_reference: {
      up: [
        {
          section: 'founder_purpose',
          checks: [
            'vision ambition coherent with risk tolerance',
            'vision time horizon within founder personal planning window',
            'what success looks like coherent with primary motivation',
          ],
        },
        {
          section: 'values',
          checks: ['vision does not require compromising a stated value'],
        },
        {
          section: 'mission',
          checks: ['vision expresses mission reaching maturity, not a disconnected commercial plan'],
        },
      ],
      down: [
        {
          section: 'value_proposition',
          checks: ['value prop supports reaching the stated vision (e.g. Tailored positioning won\'t deliver 10x-scale vision without structural change)'],
        },
        {
          section: 'customers',
          checks: ['current customer mix supports reaching vision, or vision requires a different mix'],
        },
      ],
    },
  },

  value_proposition: {
    objective:
      "To articulate what the business promises to its customers — the " +
      "specific problem it solves, for whom, the outcomes it delivers, the " +
      "market positioning through which it competes, and where it positions " +
      "itself on price. This is the commercial core of the canvas: " +
      "everything about how the business reaches, serves, and is paid by " +
      "customers flows from the clarity of this statement.",

    why_it_matters:
      "A tight value proposition becomes the reference point the whole " +
      "business works from. The founder pitches from it, sales qualifies " +
      "from it, marketing writes from it, delivery scopes from it, and " +
      "support responds from it. When it's clear, everyone is describing " +
      "the same business to the outside world. When it's vague, each " +
      "function improvises its own version — and the inconsistency " +
      "confuses customers and slows internal decisions.",

    good_answer_looks_like:
      "A clearly named problem, specific enough that customers recognise " +
      "themselves in it immediately ('scaling managed-IT firms that have " +
      "outgrown their founding tooling' beats 'businesses that need " +
      "better IT'). A target customer expressed in terms the business can " +
      "actually identify and reach. Outcomes framed as what changes for " +
      "the customer, not what the business delivers. A clear market " +
      "positioning — Reliable & efficient, Best-in-class, or Tailored to " +
      "each customer — that matches how the business actually succeeds. " +
      "A price positioning the founder can defend without discomfort.",

    probe_deeper_on:
      "Feature-lists dressed as value props. Customer descriptions that " +
      "include everyone ('SMEs, mid-market, enterprise — anyone really'). " +
      "Outcomes expressed as the founder's effort rather than the " +
      "customer's gain ('we work hard to...'). Price positioning declared " +
      "as 'mid-market' by default — probe whether the founder actually " +
      "understands what premium would require them to do differently. " +
      "Value propositions trying to claim all three market positionings — " +
      "that's not positioning, it's avoidance. Ask the founder which way " +
      "they'd win if forced to pick, and what they'd give up to hold it. " +
      "Value props that sound like a competitor's with different " +
      "branding — ask 'if your closest competitor read this, would they " +
      "have to change anything in response?'",

    conversation_archetype:
      "Start with the problem, not the product: 'of all the problems this " +
      "business could solve, which one do you most want customers to " +
      "associate you with?' Probe specificity: 'walk me through an example " +
      "customer and what their world looked like before you.' Move to the " +
      "target customer with identifying characteristics. Then outcomes: " +
      "'if this works, what's different about your customer's day, week, " +
      "or business in 12 months?' Then the market positioning question. " +
      "Most founders haven't explicitly chosen one, so introduce the " +
      "concept first: 'Most businesses that do well choose, consciously " +
      "or not, how they want to win in their market. Three broad ways: " +
      "reliable & efficient (customers know exactly what they'll get), " +
      "best-in-class (genuinely better product or expertise), or tailored " +
      "to each customer (deep relationships and fit). Most businesses are " +
      "strongest in one of these — trying to be all three usually means " +
      "being positioned in none. Which sounds most like how you actually " +
      "win?' If the founder resists choosing, probe with: 'if customers " +
      "had to describe why they choose you in one word — efficient, " +
      "best, or understood — which wins? What would you give up to hold " +
      "that?' Finally, price positioning grounded in the chosen positioning.",

    output_structure: 'grouped',

    option_paradigm:
      "Three different positionings — each a coherent answer to 'what " +
      "should the business stand for commercially?' Each positioning is " +
      "anchored on a market positioning choice and radiates out from " +
      "there: what problem, which customer, which outcomes, what price " +
      "follows if we commit to this way of winning. Founder picks the " +
      "positioning that matches how they actually want to compete. " +
      "Picking forces a strategic choice — a business trying to be all " +
      "three simultaneously isn't positioned at all.",

    archetypes: [
      {
        label: 'Reliable & efficient',
        summary:
          "We win because customers know what they'll get and at what " +
          "cost. Think IKEA or McDonald's: predictable, scalable, " +
          "cost-effective.",
      },
      {
        label: 'Best-in-class',
        summary:
          "We win because our product or expertise is genuinely better. " +
          "Think Apple or Rolls-Royce: depth, craft, and continuous " +
          "innovation.",
      },
      {
        label: 'Tailored to each customer',
        summary:
          "We win because we understand each customer deeply and adapt " +
          "to them. A bit like a good wedding planner or family " +
          "solicitor: deep knowledge, long relationships, bespoke fit.",
      },
    ],

    field_notes: {
      primary_problem_solved:
        "Free text. AI probes for specificity during the conversation.",
      target_customer_type:
        "Free text. Specificity comes from conversation.",
      top_three_benefits:
        "RESTRUCTURE from textarea to repeatable entries (1–3 entries), " +
        "each a short outcome statement. Structured makes downstream " +
        "reasoning cleaner.",
      market_positioning:
        "NEW FIELD. Chip with plain labels (Reliable & efficient / " +
        "Best-in-class / Tailored to each customer), each shown with a " +
        "one-sentence explainer in the UI, plus a short free-text defence " +
        "field ('what makes this genuinely ours?'). Single choice — " +
        "forcing the strategic choice is the point. Because positioning " +
        "is a concept most founders have not explicitly considered, the " +
        "conversation introduces it before asking — teaching comes before " +
        "choosing.",
      price_positioning:
        "Keep chip (Premium / Mid-market / Low cost) as single-choice. " +
        "Add short free-text defence field ('what makes this " +
        "defensible?') — same pattern as risk tolerance in Founder " +
        "Purpose. Chip is the visual shorthand; text is what the AI " +
        "reasons against.",
    },

    narrative_output: {
      audience: 'Customers and prospects',
      length_target: '2–3 sentences — the elevator pitch',
      purpose:
        "Copyable prose the founder can put into a website homepage, " +
        "pitch deck, or introductory meeting. Must be concrete, " +
        "outcome-led, and sound distinctive to this business — not " +
        "generic. Draws on the problem, customer, outcomes, and chosen " +
        "positioning.",
      editable: true,
    },

    user_facing_intro: {
      what:
        "What you promise your customers: the specific problem you solve, " +
        "who you solve it for, the outcomes you deliver, how you choose to " +
        "win in the market, and where you sit on price. The commercial " +
        "core of the canvas.",
      why:
        "When your value proposition is tight, everyone in the business " +
        "reads from the same page — you pitch from it, sales qualifies " +
        "from it, marketing writes from it, delivery scopes from it. When " +
        "it's vague, each function improvises its own version, customers " +
        "get mixed messages, and internal decisions slow down.",
      strong_articulation:
        "A clearly named problem, specific enough that customers " +
        "recognise themselves in it immediately. A target customer you " +
        "can actually identify and reach. Outcomes framed as what " +
        "changes for the customer, not what you deliver. A clear choice " +
        "of how you win — reliable & efficient, best-in-class, or " +
        "tailored to each customer. A price position you can defend " +
        "without winching.",
    },

    decision_guru_triggers: [
      {
        prompt: 'Should we reposition on price?',
        prompt_template:
          "Should we reposition on price — moving up-market or cutting " +
          "to attract volume — given our {market_positioning} " +
          "positioning, current {price_positioning} level, and typical " +
          "deal size of {typical_deal_size}?",
        lens: 'profit',
        triggering_conditions: {
          any_of: [
            {
              type: 'field_value',
              field: 'pricing_confidence',
              value: ['Concerned - may be overpriced', 'Concerned - definitely underpriced'],
              rationale: 'founder has surfaced pricing concerns',
            },
            {
              type: 'cross_ref_tension',
              from: 'value_proposition.price_positioning',
              to: 'value_proposition.market_positioning',
              rationale: 'price and market positioning misaligned (e.g. Tailored + Low cost)',
            },
            {
              type: 'founder_statement',
              pattern: 'considering moving up-market or cutting price',
              rationale: 'founder explicitly considering a price move',
            },
          ],
        },
        when:
          "Founder is considering moving up-market or cutting to attract " +
          "volume. Narrower than a positioning-wide decision.",
        updates_field: 'price_positioning',
      },
      {
        prompt: 'What should our market positioning be?',
        prompt_template:
          "What should our market positioning be — Reliable & efficient, " +
          "Best-in-class, or Tailored to each customer — given our " +
          "problem '{primary_problem_solved}', our target customer " +
          "'{target_customer_type}', and our {vision.target_type} " +
          "ambition?",
        lens: 'market',
        triggering_conditions: {
          any_of: [
            {
              type: 'field_absent',
              field: 'value_proposition.market_positioning',
              rationale: 'market positioning not yet chosen',
            },
            {
              type: 'cross_ref_tension',
              from: 'values',
              to: 'value_proposition.market_positioning',
              rationale: 'chosen positioning tensions with stated values',
            },
            {
              type: 'founder_statement',
              pattern: 'considering a pivot in how the business competes',
              rationale: 'founder signals strategic reconsideration',
            },
          ],
        },
        when:
          "Founder unsure or considering a pivot in how the business " +
          "competes. Updates market_positioning and typically forces " +
          "reconsideration of downstream fields.",
        updates_field: 'market_positioning',
      },
    ],

    cross_reference: {
      up: [
        {
          section: 'founder_purpose',
          checks: [
            'market positioning coherent with risk tolerance',
            'value prop coherent with primary motivation',
          ],
        },
        {
          section: 'values',
          checks: ['chosen market positioning aligns with stated values'],
        },
        {
          section: 'mission',
          checks: ['customer and problem coherent with mission beneficiary'],
        },
        {
          section: 'vision',
          checks: ['value prop supports reaching the stated vision'],
        },
      ],
      down: [
        {
          section: 'customers',
          checks: [
            'customer segments coherent with stated target customer type',
            'segment revenue mix consistent with declared market positioning',
          ],
        },
        {
          section: 'competitors',
          checks: [
            'win reasons support the claimed market positioning (e.g. Tailored positioning + win on Price is a disconnect)',
          ],
        },
        {
          section: 'differentiators',
          checks: [
            'differentiators coherent with chosen market_positioning (Tailored → relationship/customisation; Reliable & efficient → process/systemisation; Best-in-class → capability/expertise)',
          ],
        },
        {
          section: 'go_to_market',
          checks: [
            'market_positioning implies coherent motion blend (Tailored → relationship-led motions; Reliable & efficient → scalable motions; Best-in-class → Inbound + reputation)',
          ],
        },
        {
          section: 'revenue_model',
          checks: [
            'price_positioning vs typical_deal_size — Premium + low deal size is a tension; Low-cost + premium deal size is a tension',
            'market_positioning vs primary_revenue_type — Tailored typically implies project/retainer; Reliable & efficient typically implies subscription/transaction',
          ],
        },
        {
          section: 'cost_model',
          checks: [
            'market_positioning coherent with cost-category weighting (Best-in-class + low Tech/Marketing = under-investment tension)',
            'price_positioning coherent with cost shape (Premium with low Professional services might indicate scope drift)',
          ],
        },
      ],
    },
  },

  customers: {
    objective:
      "To identify who the business actually serves, how each segment " +
      "contributes commercially, and which segments represent the best " +
      "use of the business's attention and investment. Surface-level the " +
      "'who buys from us' question — but the real strategic work is " +
      "recognising that not all customers are equally valuable and the " +
      "business has to choose.",

    why_it_matters:
      "A business that treats all customers as equal ends up optimising " +
      "for none of them. Every functional area — sales, delivery, " +
      "support, pricing, product — is more effective when it knows which " +
      "customers are the priority. Without explicit segmentation, the " +
      "business defaults to treating whoever shouts loudest as most " +
      "important, which is rarely the same as who's most valuable. The " +
      "cost of this isn't immediately visible: it shows up as margin " +
      "erosion, delivery team burnout serving unprofitable accounts, and " +
      "sales chasing deals that don't fit.",

    good_answer_looks_like:
      "2–4 distinct segments (more than 4 usually means they aren't " +
      "really distinct). Each segment named in language the founder " +
      "would use internally, not marketing-speak. Revenue share " +
      "quantified, even approximately. Margin and complexity honestly " +
      "assessed — founders often protect favoured segments by rating " +
      "them higher on margin than the numbers support. Each segment " +
      "answers 'why do they buy from us in particular?' at least " +
      "implicitly.",

    probe_deeper_on:
      "'All our customers are great' — ask which ones they'd miss most " +
      "if they left tomorrow, and which they wouldn't. 'We serve SMEs' " +
      "as a single segment — ask what different types of SMEs behave " +
      "differently. Segments defined by size alone — size is usually a " +
      "proxy for something more useful. Margin self-reports without " +
      "supporting detail — probe with 'how does that compare to this " +
      "other segment, and why?' Ease-of-acquisition and " +
      "delivery-complexity scores that are all 3s — the safe middle — " +
      "probe with specifics: 'who was the hardest sale you closed last " +
      "year? Who's been the hardest customer to deliver for?'",

    conversation_archetype:
      "Start open: 'walk me through your customer base — who do you " +
      "typically sell to?' Listen carefully for signal: did the founder " +
      "name distinct types, describe a broad category, or something in " +
      "between? The AI's first job is diagnosis — which situation is " +
      "this founder in?\n\n" +
      "If they named distinct types: these ARE segments. Move straight " +
      "to confirming and capturing fields per segment.\n\n" +
      "If they described a broad category or single sector: introduce " +
      "the idea of dimensions. 'Within [sector], businesses often split " +
      "by size, by sector, by recurring-vs-project work — does any of " +
      "those feel true for your business?' Use " +
      "SECTOR_SEGMENTATION_DIMENSIONS to offer relevant dimensions.\n\n" +
      "If overlap or ambiguity: help the founder find the most useful " +
      "split, not the cleanest one. 'If you treat them the same in " +
      "practice, they're probably one segment.'\n\n" +
      "If the founder resists splitting at all: diagnostic questions. " +
      "'Think about the last 5 customers you won — did you sell to them " +
      "all the same way?' 'If you had to double the business but could " +
      "only serve half your current customer types, which half would " +
      "you keep?'\n\n" +
      "Once segments surface, work through each: revenue share, margin, " +
      "ease of acquisition, delivery complexity. Ground each score in a " +
      "real recent example. Finally, the strategic lens: 'which segment " +
      "do you most want more of? Which would you quietly be happy if " +
      "you had less of?'",

    // ── Future outlook (trend annotation + new entries) ─────────────
    // After capturing today's segments, the dialogue runs one closing
    // pass per segment to capture the future trend, then separately
    // asks about net-new segments by the vision horizon. Light touch —
    // not a second full round.
    future_outlook_instruction:
      "After today's segments are captured, run ONE closing pass that " +
      "asks per segment: 'Will this segment continue to be served? If " +
      "yes, grow / stay the same / shrink — and what's driving that?' " +
      "Keep it tight — one or two exchanges per segment, not a fresh " +
      "deep-dive. Then ONE separate question about new segments: " +
      "'Looking out to your vision horizon — what new customer " +
      "segments do you expect to serve that you don't today, and why " +
      "each one?' Don't push if the leader has no view yet; empty is " +
      "fine. These answers map into the per-segment _future block " +
      "(will_continue, trend, reasoning) and the section-level " +
      "future_additions list (name, reasoning, horizon_year).",

    // Sparingly, the LLM may propose a segment the leader might not
    // have considered — grounded in what they've actually said. Helps
    // surface 'oh, hadn't thought of that' moments without becoming
    // pushy or generic. Use at most once per conversation and only
    // after 2+ segments have been substantively described.
    proposal_guidance:
      "Once the leader has substantively described 2 or more customer " +
      "segments AND you have a grounded reason to think a related " +
      "segment might be worth exploring, you MAY (at most once in this " +
      "conversation) propose it. Frame it as a possibility, not a " +
      "recommendation: 'Worth thinking about — given your strength in " +
      "X, customers in Y segment often have the same need but are " +
      "easier to reach. Worth a slot, or not relevant?' Don't push if " +
      "they wave it off. Don't propose more than one. Never propose " +
      "generic 'enterprise' / 'mid-market' splits without a specific " +
      "reason rooted in what they've shared. The goal is an 'oh, " +
      "hadn't thought of that' moment — not a sales pitch.",

    scaffolding_tiers: [
      {
        tier: 1,
        method: 'website_inference',
        description:
          "Extend inference.js to capture proposed segments from " +
          "testimonial categories, pricing tiers, case study groupings, " +
          "'who we serve' sections. AI offers these as starting points.",
      },
      {
        tier: 2,
        method: 'sector_dimensions',
        description:
          "Use SECTOR_SEGMENTATION_DIMENSIONS to offer typical " +
          "dimensions for the founder's sector. Used when founder is " +
          "single-sector or inference didn't surface segments.",
      },
      {
        tier: 3,
        method: 'diagnostic_questions',
        description:
          "Surface differences between customers without demanding " +
          "formal segmentation. Segmentation emerges from the answers.",
      },
      {
        tier: 4,
        method: 'revisit_later',
        description:
          "If a founder genuinely sees all customers as one group, " +
          "accept for now but flag as a canvas gap worth revisiting in " +
          "6 months as the business grows.",
      },
    ],

    output_structure: 'per_entry_with_section_level_output',

    option_paradigm:
      "Per segment: AI proposes 2 ways of defining each segment (based " +
      "on what the founder said) — the sharper framing usually wins " +
      "because it tells the business more about how to sell. At the " +
      "section level, after all segments are captured, the AI proposes " +
      "3 prioritisation framings: growth-weighted, margin-weighted, or " +
      "defensive. Founder picks the framing that matches the business's " +
      "current situation. This feeds the Decision Guru trigger.",

    archetypes: [
      {
        label: 'Growth-weighted',
        summary:
          "Prioritise the segment with highest upside potential. " +
          "Invest where future value is largest, even if current " +
          "share is modest.",
      },
      {
        label: 'Margin-weighted',
        summary:
          "Prioritise the most profitable segments. Invest where the " +
          "business already makes money well, protect and deepen " +
          "those positions.",
      },
      {
        label: 'Defensive',
        summary:
          "Prioritise protecting segments where the business has " +
          "strong positions already. Retain before chasing growth.",
      },
    ],

    field_notes: {
      segment_name:
        "Free text. No changes.",
      revenue_pct:
        "Free text. AI probes for specificity but doesn't force " +
        "precision — the hedge 'roughly 40%' carries meaning.",
      typical_margin:
        "Chip (Low / Medium / High). ADD a supporting text field so " +
        "founder explains why it's that margin. Without the text, " +
        "'Medium' means nothing downstream.",
      ease_of_acquisition:
        "1–5 rating scale. Keep as-is. In conversation, AI grounds the " +
        "score in a concrete recent example.",
      delivery_complexity:
        "1–5 rating scale. Keep as-is. In conversation, AI grounds the " +
        "score in a concrete recent example.",
      strategic_intent:
        "NEW FIELD per segment. Chip: Grow / Protect / Maintain / " +
        "De-prioritise. Captures the strategic stance toward each " +
        "segment; feeds directly into Go-to-Market and the Decision " +
        "Guru prioritisation trigger.",
      prioritisation_lens:
        "NEW SECTION-LEVEL FIELD. Chip: Growth-weighted / " +
        "Margin-weighted / Defensive. Captures the chosen framing from " +
        "the option paradigm.",
    },

    narrative_output: {
      audience: 'Investor conversations, new sales hire onboarding, strategic documents',
      length_target: '3–4 sentences',
      purpose:
        "The 'who we serve' summary — segments named, approximate " +
        "revenue weighting, the chosen prioritisation lens. NOT the " +
        "elevator pitch (that's Value Proposition).",
      editable: true,
    },

    user_facing_intro: {
      what:
        "Who your business actually serves, how each group contributes " +
        "commercially, and which ones are worth the most of your " +
        "attention and investment. Not just 'who buys from us' — the " +
        "harder question is which customers should get the business's " +
        "energy.",
      why:
        "A business that treats all customers as equal ends up " +
        "optimising for none of them. Sales, delivery, support, pricing — " +
        "all of it gets sharper when everyone knows who matters most. " +
        "Without this clarity, you default to treating whoever shouts " +
        "loudest as most important, which is rarely the same as who's " +
        "most valuable.",
      strong_articulation:
        "Two to four distinct customer groups — more than that usually " +
        "means the groups aren't really distinct. Named in your own " +
        "internal language, not marketing-speak. Revenue share " +
        "quantified, even approximately. An honest read on margin and " +
        "complexity — the temptation is to rate favourite customers " +
        "better than the numbers actually support.",
    },

    decision_guru_triggers: [
      {
        prompt: 'Which customer segments should we prioritise and invest in?',
        prompt_template:
          "Which customer segments should we prioritise and invest in, " +
          "given our current segments ({segment_summary}), our " +
          "{market_positioning} positioning, and our " +
          "{vision.target_detail} growth ambition?",
        lens: 'market',
        triggering_conditions: {
          any_of: [
            {
              type: 'field_absent',
              field: 'customers.prioritisation_lens',
              rationale: 'no prioritisation lens chosen yet',
            },
            {
              type: 'field_count',
              field: 'customers.entries',
              comparator: '>= 2',
              rationale: 'multiple segments exist — prioritisation is meaningful',
            },
            {
              type: 'founder_statement',
              pattern: 'spreading too thin across customer groups',
              rationale: 'founder signals capacity-allocation concern',
            },
          ],
        },
        when:
          "Founder wants to explicitly choose where to concentrate. " +
          "Typically run after segments are captured; updates " +
          "prioritisation_lens and per-segment strategic_intent fields.",
        updates_field: 'prioritisation_lens',
      },
    ],

    cross_reference: {
      up: [
        {
          section: 'mission',
          checks: ['prioritised segments coherent with mission beneficiary'],
        },
        {
          section: 'vision',
          checks: ['current customer mix supports reaching vision, or vision requires a different mix'],
        },
        {
          section: 'value_proposition',
          checks: [
            'customer segments coherent with stated target customer type',
            'segment revenue mix consistent with declared market positioning',
          ],
        },
      ],
      down: [
        {
          section: 'go_to_market',
          checks: [
            'segment strategic_intent drives motion investment — Grow segments need motion investment; De-prioritise segments should not be consuming motion capacity',
          ],
        },
        {
          section: 'revenue_model',
          checks: [
            'segment typical_margin should roughly match gross_margin at Revenue Model section level — divergence is a flag',
          ],
        },
      ],
    },
  },

  competitors: {
    objective:
      "To map the alternatives available to customers — direct competitors, " +
      "substitutes, and the 'do nothing' option — and to understand " +
      "honestly why this business wins and loses against each. Not a " +
      "comprehensive competitive analysis; a grounded view of how " +
      "customers actually choose between this business and what else is " +
      "out there.",

    why_it_matters:
      "Most founders either over- or under-weight competitors. Over-" +
      "weighting turns strategy into a reactive game of matching what " +
      "everyone else does; under-weighting means customer objections ('why " +
      "you over X?') catch the business flat-footed. The middle ground is " +
      "knowing exactly who the business competes with, why it wins when " +
      "it wins, and — crucially — why it loses when it loses. That last " +
      "part is where most founders avoid looking, and it's where the real " +
      "strategic information lives. A clear-eyed view also informs " +
      "pricing, positioning, sales collateral, and hiring for commercial " +
      "roles.",

    good_answer_looks_like:
      "A mix of alternative types, not just direct competitors — customers " +
      "often choose between you and doing nothing, or between you and a " +
      "cheaper alternative, or between you and building it in-house. " +
      "Named examples where possible. An honest reading of win/lose " +
      "patterns — founders who say 'we win most of the time' usually " +
      "haven't tracked it properly or are only hearing about the wins. A " +
      "clear articulation of what makes the business win and, " +
      "distinguishably, what makes it lose.",

    probe_deeper_on:
      "'We don't really have competitors' — almost always wrong. Probe: " +
      "'what do customers do instead of buying from you? Does nothing " +
      "ever happen? Does someone internal take it on?' That's still a " +
      "competitor. Over-focus on direct rivals while missing substitutes. " +
      "Win/lose patterns expressed only as wins — push gently: 'tell me " +
      "about the last deal you lost and why you think the customer went " +
      "the other way'. 'We win on quality' — probe what specifically, " +
      "because everyone claims quality. 'We win on price' from a self-" +
      "declared premium business — surface the tension openly.",

    conversation_archetype:
      "Start with the customer's point of view, not the market map. " +
      "'When a prospect is deciding, what are the actual alternatives in " +
      "their head? Not just who you compete with — who or what do they " +
      "compare you against?' Let substitutes and status-quo surface " +
      "naturally. Then name specific examples per alternative type. Move " +
      "to win/lose patterns: for each type of alternative, 'when do you " +
      "win? When do you lose?' Ground each answer in a specific recent " +
      "example. For win_reason and lose_reason chips other than Price " +
      "(which is self-explanatory), probe for a specific recent example — " +
      "'you said Expertise; tell me about a recent win where expertise " +
      "was clearly the deciding factor'. At the end, offer the emerging-" +
      "threats Decision Guru journey as a separate exercise: 'You've " +
      "mapped the current landscape. Some founders also find it useful " +
      "to think about emerging alternatives — new technology, new " +
      "entrants, changing customer behaviour. Want to explore that as a " +
      "separate exercise?'",

    // ── Future outlook (trend annotation + new entries) ─────────────
    future_outlook_instruction:
      "After today's landscape is mapped, run ONE closing pass on the " +
      "future. Ask: 'Will today's competitive picture hold over your " +
      "vision horizon? Stable, largely (some shift), or material " +
      "change?' Then probe what's driving the shift — new entrants, " +
      "AI-native challengers, customer behaviour changing, sector " +
      "consolidation. Capture each shift identified plus a one-line " +
      "reason. Then ONE separate question for new competitors: 'What " +
      "competitors or threats do you anticipate facing by your vision " +
      "horizon that you don't face today, and why each one credibly?' " +
      "Don't manufacture threats — empty is fine if the leader has no " +
      "view. Maps to the section-level _future block (will_continue, " +
      "trend, reasoning) and future_additions list.",

    output_structure: 'grouped',

    option_paradigm:
      "Proposal-review paradigm instead of options. Competitors is " +
      "descriptive, not a strategic choice — three framings of a " +
      "competitive landscape doesn't make sense. After the conversation, " +
      "the AI proposes a single integrated read of the landscape with " +
      "its reasoning shown: 'Based on what you said, here's how I'd " +
      "characterise your competition — alternative types, named " +
      "examples, win/lose patterns and reasons.' Founder accepts, " +
      "edits, or challenges individual elements. Closer to the old " +
      "ProposedReview flow but now with genuine reasoning visible, not " +
      "just extraction.",

    field_notes: {
      alternative_types:
        "Multi-select chip. Options: Direct competitors / Lower-cost / " +
        "Premium / In-house / DIY / Do nothing. Keep as-is. Emerging/" +
        "future threats deliberately NOT included here — those are a " +
        "separate Decision Guru journey to keep this section descriptive " +
        "and the LLM hallucination risk lower.",
      key_examples:
        "RESTRUCTURE from free text to tagged entries. Each example is " +
        "tagged to its alternative_types value — 'Company X' tagged as " +
        "'Direct competitor', 'building in-house' tagged as 'In-house'. " +
        "Makes downstream reasoning richer (e.g. 'the founder's biggest " +
        "competitor by their own description is status-quo — what's the " +
        "Go-to-Market implication?').",
      customer_drivers:
        "Multi-select chip: why customers choose competitors (not us). " +
        "EXPAND options to: Price / Quality / Service / Reputation / " +
        "Familiarity / Speed / Local presence. Speed and Local presence " +
        "come up often in SME competitive dynamics.",
      win_pattern:
        "Single-choice chip (Win often / Win sometimes / Lose often / " +
        "Rarely compete directly). Self-report — treat as a founder " +
        "self-check, not a primary input. The actual reasoning in " +
        "win_reason / lose_reason is what the AI reasons against.",
      win_reason:
        "Multi-select chip: Price / Risk reduction / Speed / Brand / " +
        "Relationships / Expertise. Add a supporting 'describe a recent " +
        "win' text field ONLY when the selected reason is one of: Risk " +
        "reduction, Speed, Brand, Relationships, Expertise. Price is " +
        "self-explanatory and skips the defence prompt.",
      lose_reason:
        "NEW FIELD. Mirror of win_reason — multi-select chip with the " +
        "same options. Same defence-text logic (applies to all options " +
        "except Price). Surfacing the lose pattern is arguably more " +
        "strategically valuable than the win pattern — most founders " +
        "have thought about wins and avoided losses.",
    },

    narrative_output: {
      audience: 'Sales enablement, investor decks, internal strategy documents',
      length_target: '2–3 sentences',
      purpose:
        "A short competitive-position paragraph. Example shape: 'We " +
        "compete primarily against X and Y, but our biggest actual " +
        "competitor is often the customer deciding to handle it " +
        "in-house. We win when [reason]; we lose when [reason].'",
      editable: true,
    },

    decision_guru_triggers: [
      {
        prompt: 'Which emerging threats deserve your attention?',
        prompt_template:
          "Which emerging threats deserve our attention, given our " +
          "current competitive landscape ({alternative_types}, winning " +
          "on {win_reason}, losing on {lose_reason}) and our " +
          "{market_positioning} positioning?",
        lens: 'market',
        triggering_conditions: {
          any_of: [
            {
              type: 'user_initiated',
              surface_point: 'end of Competitors section',
              rationale: 'offered as optional follow-up once landscape is mapped — not automatic',
            },
            {
              type: 'founder_statement',
              pattern: 'concern about new entrants or technology change',
              rationale: 'founder explicitly raises forward-looking threat',
            },
          ],
        },
        when:
          "Founder wants to explicitly think about where the competitive " +
          "landscape is heading. Separate from the descriptive Competitors " +
          "section to keep that clean and avoid LLM hallucination risk " +
          "from mixing historical mapping with speculative scanning.",
        updates_field: null,
      },
    ],

    user_facing_intro: {
      what:
        "The alternatives available to your customers — direct rivals, " +
        "cheaper or premium substitutes, building it in-house, and doing " +
        "nothing — and an honest read on why you win when you win, and " +
        "why you lose when you lose.",
      why:
        "Most founders either over-weight competitors (reactive " +
        "strategy) or under-weight them (surprised by customer " +
        "objections). The useful middle is knowing exactly who you're " +
        "up against and why customers actually choose between you. " +
        "Losses matter more than wins here — that's where the real " +
        "strategic information sits, and it's the part founders most " +
        "often avoid looking at.",
      strong_articulation:
        "A mix of alternative types, not just named rivals — include " +
        "status-quo and substitutes, because that's often where the real " +
        "competition is. Specific examples where possible. Win and lose " +
        "patterns grounded in recent real deals, not self-assessment in " +
        "the abstract.",
    },

    cross_reference: {
      up: [
        {
          section: 'mission',
          checks: ['competitors are those fighting for the mission beneficiary\'s attention, not rivals from a different market'],
        },
        {
          section: 'value_proposition',
          checks: [
            'win reasons support the claimed market positioning (e.g. Tailored positioning + win on Price is a disconnect)',
            'competitive landscape consistent with the stated target customer',
          ],
        },
      ],
      down: [
        {
          section: 'differentiators',
          checks: [
            'differentiators track the win_reason pattern captured in Competitors (claim to win on Expertise but list differentiators around Price = live contradiction)',
          ],
        },
        {
          section: 'go_to_market',
          checks: [
            'GTM differentiates from competitor GTM or matches it — matching is not wrong but worth being aware of',
          ],
        },
      ],
    },
  },

  differentiators: {
    objective:
      "To identify the small number of specific, defensible things that " +
      "make this business genuinely different from its alternatives — and " +
      "to pressure-test which of those the business should lead with in " +
      "sales, marketing, and strategic conversations.",

    why_it_matters:
      "Differentiators are what sales teams open with, what marketing " +
      "leans on, what investors probe, and what competitors try to blunt. " +
      "Most SME founders didn't start their business on a defensible " +
      "moat — they started because they knew a sector well and thought " +
      "they could do it a little better or a little cheaper than the " +
      "alternatives. That's a real and common starting point, not a " +
      "weakness, and the section should treat it as such. What the " +
      "section is genuinely for is honest self-assessment: naming what " +
      "makes customers actually choose the business (even if modest), " +
      "rating how hard each thing would be for a competitor to copy, " +
      "and — crucially — deciding whether to stay where you are or " +
      "deliberately build toward something more defensible. Both answers " +
      "are legitimate. What matters is that the choice is made openly, " +
      "not assumed or dressed up as something stronger.",

    good_answer_looks_like:
      "Two patterns are both valid. Pattern one — 2–4 genuine " +
      "differentiators, each expressed in language a customer would " +
      "recognise, each backed by concrete evidence, each honestly rated " +
      "for defensibility, one named as the lead. Pattern two — an honest " +
      "acknowledgement that the current edge is modest (sector " +
      "knowledge, execution, local reputation, small price or service " +
      "advantage), named plainly as 'not really defensible for the long " +
      "run' rather than dressed up as something it isn't. Pattern two " +
      "is a common SME starting position; the section should treat it " +
      "as a waypoint toward something more deliberate, not a failure. " +
      "Where the current answer lands between the two patterns, the " +
      "honesty matters as much as the structure.",

    probe_deeper_on:
      "'Our quality', 'our service', 'our people' — these are often " +
      "named honestly by founders competing on execution in a sector " +
      "they understand well. That's a real edge in the short run, but " +
      "not a moat — a competent competitor can match it given time. " +
      "Worth naming plainly as 'edge, not moat' rather than dismissing " +
      "or inflating. Features mistaken for differentiators — a feature " +
      "is something the product does; a differentiator is something " +
      "that makes customers choose you over alternatives. Internal " +
      "virtues the customer never experiences ('we really care about " +
      "X') — if the customer can't tell the difference, it isn't one; " +
      "probe for where the caring actually shows up in the customer " +
      "experience. Things that used to differentiate but no longer do — " +
      "the market has caught up, or the founder is repeating a line " +
      "that was true five years ago. 'Trust' or 'relationships' as a " +
      "differentiator without specifics — everyone claims those; probe " +
      "for the concrete mechanism behind them, which is often the real " +
      "edge. The overall posture is non-judgemental: help the founder " +
      "see honestly what they have, including recognising when the " +
      "answer is genuinely 'not much that's defensible — we just know " +
      "this sector and execute well'. That's a valid answer and a " +
      "natural entry into the deliberate-differentiator Decision Guru " +
      "journey.",

    conversation_archetype:
      "Start from the customer, not the internal view: 'When a customer " +
      "chooses you — in their own words — what do they credit? Not " +
      "quality, not service — what do they actually say?' Let candidate " +
      "differentiators emerge from stories of specific recent wins. For " +
      "each candidate, offer two framings — sharper and more generic — " +
      "and let the founder feel the difference ('Deep sector knowledge' " +
      "vs 'We've been in this market for 12 years and know the specific " +
      "compliance patterns that trip up new entrants'). Probe evidence " +
      "per entry: 'what's the concrete example of this?' Probe " +
      "defensibility per entry: 'if your closest competitor read this " +
      "list tomorrow, how many could they credibly claim? What would it " +
      "take them to do so?' That question maps naturally to the " +
      "defensibility chip. Probe durability: 'what would have to change " +
      "for this to stop being true in three years?' If the answer is " +
      "nothing, it probably isn't genuinely defensible. Add the coherence " +
      "probe mid-conversation: 'in Competitors you said you win on [X, " +
      "Y, Z]. Do your stated differentiators reflect those same drivers, " +
      "or is there a gap between what you think makes you different and " +
      "what customers actually choose you on?' Before the closing " +
      "pressure test, run a POSTURE CHECK: if most candidates have rated " +
      "as 'Not really defensible — time advantage only', or the founder " +
      "has named their edge openly as sector knowledge plus execution, " +
      "surface it without judgement and offer the deliberate-" +
      "differentiator journey: 'Looking at what we've mapped, your " +
      "strongest edges are mostly knowing this sector well and executing " +
      "better than average — which is how many strong SMEs start, and " +
      "it's a real edge today. It's also not something a well-funded " +
      "competitor couldn't replicate given time. Worth thinking " +
      "deliberately about what harder-to-copy differentiator you could " +
      "build toward over the next two or three years? That's a separate " +
      "piece of work we can do whenever suits.' The DG journey is " +
      "offered as forward-looking strategic work, not remediation. " +
      "Close with the pressure test: 'if you could only lead with one " +
      "in a sales conversation — only one — which?' That becomes the " +
      "lead differentiator.",

    // ── Future outlook (trend annotation + new entries) ─────────────
    future_outlook_instruction:
      "After today's differentiators are captured and the lead pick is " +
      "made, run ONE closing pass per differentiator: 'Will this still " +
      "differentiate by your vision horizon? If yes, strengthen or " +
      "maintain — and what's driving that?' Keep tight, one or two " +
      "exchanges per entry. Then ONE separate question: 'What new " +
      "differentiators do you need to build that you don't have today, " +
      "to compete at the next stage?' This is genuinely strategic — if " +
      "current edges are mostly sector knowledge + execution (a common " +
      "SME starting position), this question becomes the bridge into " +
      "the deliberate-differentiator DG journey. Don't manufacture " +
      "future differentiators if the leader doesn't see them yet; " +
      "empty is honest. Maps to per-entry _future block and section-" +
      "level future_additions list.",

    output_structure: 'per_entry_with_section_level_output',

    option_paradigm:
      "Per-entry: for each candidate differentiator surfaced, AI proposes " +
      "two framings — one sharper and more specific, one that reads more " +
      "generic — and the founder picks the one that rings truest. Mirror " +
      "exercise pattern, lighter than Decision Guru options. No section-" +
      "level option paradigm — the lead_differentiator pick is a " +
      "pressure-test output captured as data, not a three-framing choice.",

    field_notes: {
      differentiator:
        "Per entry. Free text. RENAME from the existing `unique_angle` " +
        "field for clarity — 'unique angle' implies marketing-speak; " +
        "'differentiator' is the precise term. Each entry captures one " +
        "specific point of difference. Data migration: copy existing " +
        "unique_angle text into the first differentiator entry's text " +
        "field; leave other per-entry fields empty and prompt completion " +
        "on next visit.",
      evidence:
        "Per entry. Free text. A specific example, pattern, or capability " +
        "that grounds the differentiator. Without evidence, a " +
        "differentiator is aspiration.",
      defensibility_type:
        "Per entry. NEW CHIP. Single-choice. Four options: 'Hard to copy " +
        "(structural)' — process, IP, physical asset, accreditation, " +
        "geographic footprint; 'Hard to copy (human / cultural)' — team " +
        "expertise, culture, long-standing relationships, institutional " +
        "knowledge; 'Hard to copy (cost-prohibitive)' — replicating would " +
        "require investment competitors won't make; 'Not really " +
        "defensible — time advantage only' — replicable in under 12–18 " +
        "months. The honest option matters — naming 'time advantage only' " +
        "is strategically useful (signals where defensive investment is " +
        "urgent), not a weakness to hide.",
      defensibility_detail:
        "Per entry. Free text supporting the chip. 'What specifically " +
        "makes this hard to copy?' — the evidence behind the rating. " +
        "Matches the defence-text pattern used on market_positioning and " +
        "price_positioning in Value Proposition.",
      lead_differentiator:
        "Section-level. NEW FIELD. Reference to one of the differentiator " +
        "entries (by index or id). The one the business leads with in " +
        "sales conversations. Captured as the output of the closing " +
        "pressure test. Single choice — forcing the pick is the point.",
    },

    narrative_output: {
      audience: 'Prospects, sales collateral, website copy, and new commercial hire onboarding',
      length_target: '2–3 sentences',
      purpose:
        "A short 'what makes us different' paragraph that integrates the " +
        "lead differentiator with the supporting ones, phrased for a " +
        "prospect audience — outcome-led, specific, and honest about what " +
        "the business actually is versus aspires to be.",
      editable: true,
    },

    decision_guru_triggers: [
      {
        prompt: 'How could we build a more deliberate differentiator?',
        prompt_template:
          "How could we build a more deliberate and defensible " +
          "differentiator over the next 2–3 years, given our " +
          "{vision.target_type} ambitions ({vision.target_detail})?",
        lens: 'market',
        triggering_conditions: {
          any_of: [
            {
              type: 'field_majority',
              field: 'differentiators.entries[].defensibility_type',
              value: 'time_advantage_only',
              rationale: 'current differentiators are mostly not defensible',
            },
            {
              type: 'cross_ref_tension',
              from: 'vision',
              to: 'differentiators',
              rationale: 'vision ambition exceeds current defensibility',
            },
            {
              type: 'founder_statement',
              pattern: 'edge is sector knowledge plus execution',
              rationale: 'founder openly names edge as non-structural',
            },
          ],
        },
        when:
          "Forward-looking strategic journey, not remediation. Triggered " +
          "when the current differentiators mostly rate 'Not really " +
          "defensible — time advantage only', or when the founder " +
          "explicitly names their edge as sector knowledge plus execution " +
          "with no structural moat. Common and valid SME starting " +
          "position. The journey invites the deliberate choice to invest " +
          "in building something harder to copy — typically over a 2–3 " +
          "year horizon — and produces either a new candidate " +
          "differentiator entry or a named strategic intent to invest.",
        updates_field: null,
      },
    ],

    user_facing_intro: {
      what:
        "The two to four specific things that make your business " +
        "genuinely different from the alternatives your customers weigh " +
        "up — each one evidenced, each one honestly rated for how hard " +
        "it is to copy, and one clearly named as the one you'd lead with.",
      why:
        "Every commercial artefact downstream — sales pitches, marketing " +
        "copy, investor conversations, hiring pitches — leans on these. " +
        "Many strong SMEs start by being a solid operator in a sector " +
        "they know, with modest edges on execution or price. That's a " +
        "real starting point and a valid answer here. What matters is " +
        "being honest about what you've got, including naming it plainly " +
        "when the edge is 'we just know this sector and execute well' — " +
        "that's a common position, not a failure, and it's a natural " +
        "entry point into deliberately building toward something harder " +
        "to copy, if that's a direction you want to go.",
      strong_articulation:
        "Two to four entries, not a long list — more than that usually " +
        "means features dressed as differentiators. Each expressed in " +
        "language a customer would recognise, not internal jargon. Each " +
        "backed by a specific real example. Each honestly rated for " +
        "defensibility. One clearly named as the lead.",
    },

    cross_reference: {
      up: [
        {
          section: 'mission',
          checks: ['differentiators serve what the mission says the business is for, not a disconnected commercial narrative'],
        },
        {
          section: 'value_proposition',
          checks: [
            'differentiators coherent with chosen market_positioning (Tailored → relationship/customisation; Reliable & efficient → process/systemisation; Best-in-class → capability/expertise; mismatch is a real tension)',
          ],
        },
        {
          section: 'competitors',
          checks: [
            'differentiators track the win_reason pattern captured in Competitors (claim to win on Expertise but list differentiators around Price = live contradiction)',
          ],
        },
      ],
      down: [
        {
          section: 'go_to_market',
          checks: [
            'lead_differentiator shows up in the primary sales message of the primary_motion',
          ],
        },
      ],
    },
  },

  go_to_market: {
    objective:
      "To make explicit how the business actually acquires customers, " +
      "how it retains them commercially, and whether the mix of motions " +
      "matches the market positioning and customer strategy declared " +
      "earlier in the canvas.",

    why_it_matters:
      "Most SME founders can describe a channel or two that works, but " +
      "the full commercial motion — the blend, the primary weight, the " +
      "retention side — tends to live in their head as a loose impression " +
      "rather than explicit strategy. Making it explicit tests three " +
      "kinds of coherence: does the acquisition motion match the " +
      "positioning (a Tailored, relationship-led business doing 70% paid " +
      "inbound is strategically tensioned); is the sales motion " +
      "sustainable (founder-led works up to a ceiling — knowing where " +
      "that ceiling is matters); is there a retention side at all (many " +
      "SMEs pour energy into acquisition and let retention happen by " +
      "accident, which is cheap strategy until a customer base gets big " +
      "enough to leak). The stated motion also informs hiring, unit " +
      "economics, and the next sensible investment.",

    good_answer_looks_like:
      "An acquisition blend that reflects observable reality — the last " +
      "10 closed deals, not the aspiration. A primary motion that's " +
      "earned its primacy — the founder can point to specific wins it " +
      "produced. A sales motion named honestly — founder-led is fine; " +
      "claiming 'we have a sales team' when it's the founder plus a " +
      "part-time assistant isn't. A retention read that distinguishes " +
      "between customers staying because of a deliberate motion and " +
      "customers staying because inertia hasn't yet turned them away. " +
      "CAC and sales cycle ranges given where known; 'unclear' is better " +
      "than a made-up number.",

    probe_deeper_on:
      "'We have a good mix' — probe what specifically the mix is, because " +
      "'mix' often means 'no primary and no concentration'. 'Word of " +
      "mouth drives our pipeline' without evidence of mechanism — probe: " +
      "'when was the last time a referral closed? How did it arrive? " +
      "What triggered the referrer?' If there's no mechanism behind it, " +
      "it's still Word of mouth but name it honestly as ambient rather " +
      "than systematic. 'Content drives pipeline' — probe whether content " +
      "closes or just fills top of funnel; attribution to content is " +
      "often generous. 'Our retention is great, customers stay for " +
      "years' — probe what specifically makes them stay, and what " +
      "percentage of revenue actually comes from prior customers vs new. " +
      "Acquisition attention dominating retention completely — surface " +
      "openly: 'you've just spent ten minutes on how you find customers " +
      "and two sentences on what happens after. Is that the right balance " +
      "of attention?'",

    conversation_archetype:
      "Teach the frame before asking. Most SME founders haven't " +
      "explicitly considered that GTM comes in motion shapes. Brief " +
      "introduction: 'Businesses tend to win customers through one of " +
      "four broad motions, usually with one dominant and others " +
      "supporting: going directly to prospects, pulling them in through " +
      "content, partnering with others who introduce you, or being " +
      "recommended through reputation and word of mouth. Most " +
      "businesses are a blend. The mix matters more than any single " +
      "channel.' Then extract the blend from stories, not self-" +
      "description: 'Think about the last 10 closed deals. How did each " +
      "one actually arrive at your door?' Categorise into the four " +
      "motions. Propose a starting allocation; founder adjusts the " +
      "sliders. Pressure-test: 'Your blend shows 45% Direct outreach and " +
      "40% Partners & referrals — which is actually pulling weight when " +
      "you look at the deals that mattered most? If you had to drop one " +
      "tomorrow, which would hurt more?' That forces the primary_motion " +
      "pick. Separately capture sales motion — different question, often " +
      "confused with acquisition channel. Sales cycle and CAC last, " +
      "accepting 'unclear' if it is. Pivot to retention deliberately: " +
      "'Now the other half of commercial reality — what happens after a " +
      "customer buys?' Let retention mechanisms emerge. Probe whether " +
      "retention is deliberate or ambient. Ask for the honest read on " +
      "how retention is going. Close with cross-reference probes: blend " +
      "vs market_positioning; primary_motion vs lead_differentiator; " +
      "acquisition investment vs segment strategic_intent.",

    // ── Future outlook (trend annotation + new entries) ─────────────
    future_outlook_instruction:
      "After today's GTM is mapped, run ONE closing pass: 'Will this " +
      "motion hold over your vision horizon? Same motion at scale, " +
      "small adjustments, or major shift?' Then probe the likely " +
      "shifts — staying founder-led, tilting to inbound, tilting to " +
      "partners, tilting to direct outreach, building a sales team, " +
      "adding self-serve. Capture each shift plus one line on what's " +
      "forcing the change (scale ceiling, segment mix, positioning). " +
      "Then ONE separate question for new motions: 'What channels or " +
      "motions do you expect to add by your vision horizon that you " +
      "don't use today, and why each one unlocks the next stage?' " +
      "Don't push if the leader's view is hazy; empty is fine. Maps " +
      "to the section-level _future block and future_additions list.",

    output_structure: 'grouped',

    option_paradigm:
      "weighted_blend_with_pressure_test — a NEW paradigm for the " +
      "canvas. Reuses the existing `split` question widget already " +
      "implemented in Decision Guru (backend/services/questions.js, " +
      "used for primary_delivery_type and fixed_costs_breakdown). AI " +
      "proposes a starting percentage allocation across the four " +
      "motions based on conversation; founder adjusts via sliders " +
      "summing to 100%; AI then runs the pressure test ('which is " +
      "actually pulling weight?') to capture a single primary_motion. " +
      "Both data points are kept: the blend describes reality; the " +
      "primary pick is the strategic anchor for downstream coherence " +
      "checks.",

    field_notes: {
      acquisition_motion_blend:
        "NEW PRIMARY FIELD. Slider allocation summing to 100% across " +
        "four motions: Direct outreach (cold outbound, founder-led " +
        "targeting, ABM); Inbound content (SEO, content, PR, thought " +
        "leadership); Partners & referrals (channel, affiliates, formal " +
        "referral programmes); Word of mouth (organic community, " +
        "network, recommendations without a formal programme). AI " +
        "proposes starting allocation from conversation; founder " +
        "adjusts. Reuses the split question type already in questions.js.",
      primary_motion:
        "NEW. Single-choice reference to one of the four blend motions. " +
        "Display label: 'What's actually pulling weight'. Output of the " +
        "pressure-test question 'which is actually pulling weight?'. " +
        "Forcing the pick is the strategic anchor for cross-reference " +
        "coherence checks.",
      sales_approach:
        "KEEP existing field. Single-choice chip: Founder-led / In-house " +
        "sales / Self-serve / Partner-mediated / Mixed. Display label: " +
        "'How sales actually happens'. Orthogonal to acquisition motion " +
        "— any sales motion can serve any blend. Common SME inflection " +
        "point ('should we hire our first salesperson?') makes this a " +
        "DG trigger anchor.",
      avg_sales_cycle:
        "KEEP existing field. Free text, range acceptable. Display label: " +
        "'Typical sales cycle'. Accept 'unclear' if it is.",
      customer_acquisition_cost:
        "KEEP existing field. Free text, range acceptable. Display label: " +
        "'Rough CAC (if known)'. Most SMEs don't track precisely — " +
        "inviting a range or 'unclear' is honest.",
      retention_mechanism:
        "NEW. Multi-select chip. Display label: 'How customers stay'. " +
        "Options: Contracted renewal (contracts that renew); Customers " +
        "come back (no contract but they return); Growing the account " +
        "(existing customers buying more over time); Customer referrals " +
        "(happy customers bring new ones); None — transactional " +
        "(one-and-done by design).",
      retention_approach:
        "NEW. Single-choice chip. Display label: 'Retention approach'. " +
        "Options: Deliberate (a named process, role, or rhythm); Ad hoc " +
        "(mattered but no specific mechanism); Not really a focus " +
        "(transactional model by design; retention not pursued). The " +
        "Deliberate vs Ad hoc distinction surfaces where retention is " +
        "being left to inertia.",
      retention_read:
        "NEW. Free text. Display label: 'How retention is going'. " +
        "Qualitative — churn % deliberately not required. Invite the " +
        "founder's honest read with evidence where they have it.",
      acquisition_channel:
        "DEPRECATE. Replaced by acquisition_motion_blend + primary_motion. " +
        "Data migration: existing single-pick values map to the " +
        "corresponding motion at 80%, remaining 20% distributed evenly " +
        "across the other three; founder prompted to adjust on next visit.",
    },

    narrative_output: {
      audience: 'Investor conversations, prospective commercial-lead hires, and the internal operating plan',
      length_target: '2–3 sentences',
      purpose:
        "A short commercial-motion summary. Example shape: 'We acquire " +
        "primarily through [motion] — [specific evidence] — with " +
        "[secondary motion] as a supporting channel. Sales is " +
        "[founder-led / team-sold / self-serve]. Retention is " +
        "[deliberate / ad hoc / transactional], anchored on [mechanism].'",
      editable: true,
    },

    decision_guru_triggers: [
      {
        prompt: 'Should we shift from founder-led to hired sales?',
        prompt_template:
          "Should we shift from {sales_approach} to hired sales, given " +
          "our {primary_motion} motion and sales cycle of " +
          "{avg_sales_cycle}?",
        lens: 'team',
        triggering_conditions: {
          any_of: [
            {
              type: 'field_equals',
              field: 'go_to_market.sales_approach',
              value: 'Founder-led',
              rationale: 'founder-led at risk of being ceiling',
            },
            {
              type: 'founder_statement',
              pattern: 'founder time is the bottleneck',
              rationale: 'founder explicitly names capacity limit',
            },
          ],
        },
        when:
          "Major SME inflection. Typically triggered when founder-led " +
          "sales is visibly hitting a ceiling or founder time is the " +
          "bottleneck. Updates sales_approach and typically cascades " +
          "into Operating Model (hiring).",
        updates_field: 'sales_approach',
      },
      {
        prompt: 'Should we add a new acquisition motion?',
        prompt_template:
          "Should we add a new acquisition motion? Our current blend " +
          "is {acquisition_motion_blend_summary} with {primary_motion} " +
          "as our primary.",
        lens: 'market',
        triggering_conditions: {
          any_of: [
            {
              type: 'founder_statement',
              pattern: 'current channel is maxing out',
              rationale: 'dominant motion nearing saturation',
            },
            {
              type: 'cross_ref_tension',
              from: 'customers.segment_strategic_intent',
              to: 'go_to_market.acquisition_motion_blend',
              rationale: 'segment growth intent not matched by channel investment',
            },
          ],
        },
        when:
          "Existing motion is maxing out or a new one has started " +
          "showing signal. Updates acquisition_motion_blend.",
        updates_field: 'acquisition_motion_blend',
      },
    ],

    user_facing_intro: {
      what:
        "How your business actually wins customers and keeps them — the " +
        "blend of motions doing the work, the one doing the heaviest " +
        "lifting, and how deliberate your retention is. Both sides " +
        "matter. Many businesses give acquisition all the attention and " +
        "let retention happen by accident.",
      why:
        "The blend of motions tells you where to invest next, and " +
        "whether your stated positioning is actually matching what's " +
        "happening in the market. The retention side tells you how much " +
        "of your revenue you're rebuilding each year versus compounding " +
        "on what you already have. Getting this explicit sharpens " +
        "hiring decisions, investor conversations, and where your " +
        "energy goes next.",
      strong_articulation:
        "A blend that reflects the last 10 actual deals, not the " +
        "aspiration. One motion clearly named as primary, with a " +
        "specific reason why. A retention read that separates " +
        "deliberate retention from inertia. Honest on what you don't " +
        "yet measure — 'unclear' on CAC is fine; a made-up number is " +
        "worse than useless.",
    },

    cross_reference: {
      up: [
        {
          section: 'founder_purpose',
          checks: [
            'GTM aggressiveness coherent with stated risk tolerance',
            'retention investment coherent with time horizon (a 3-year exit makes retention investment harder to justify than indefinite)',
          ],
        },
        {
          section: 'value_proposition',
          checks: [
            'market_positioning implies coherent motion blend (Tailored → relationship-led motions; Reliable & efficient → scalable motions; Best-in-class → Inbound + reputation)',
          ],
        },
        {
          section: 'customers',
          checks: [
            'segment strategic_intent drives motion investment — Grow segments need motion investment; De-prioritise segments should not be consuming motion capacity',
          ],
        },
        {
          section: 'differentiators',
          checks: [
            'lead_differentiator shows up in the primary sales message of the primary_motion',
          ],
        },
        {
          section: 'competitors',
          checks: [
            'GTM differentiates from competitor GTM or matches it — matching is not wrong but worth being aware of',
          ],
        },
      ],
      down: [
        {
          section: 'revenue_model',
          checks: [
            'CAC vs typical_deal_size gives a unit-economics payback read',
            'recurring revenue models with high CAC need renewal recovery',
          ],
        },
        {
          section: 'cost_model',
          checks: [
            'sales_approach has direct cost implications — In-house sales adds People fixed cost; Founder-led caps scale',
            'CAC should recover from typical_deal_size and cost base together',
          ],
        },
      ],
    },
  },

  revenue_model: {
    objective:
      "To surface how money actually flows from customers to the " +
      "business — the mix of revenue types, the primary type driving " +
      "the business forward, and the unit economics (deal size, payment " +
      "terms, gross margin) that shape operating cash and profitability.",

    why_it_matters:
      "Revenue model shape dictates operating rhythm more than almost " +
      "anything else. Project-heavy businesses run in feast/famine " +
      "cycles with team mobilisation challenges; retainer-heavy " +
      "businesses are stable but growth-capped; subscription-heavy " +
      "businesses compound but leak through churn. Getting the blend " +
      "explicit makes hiring decisions, pricing choices, and unit " +
      "economics conversations coherent rather than disconnected. Gross " +
      "margin against price positioning is where most SME founders " +
      "discover they're underpricing; payment terms against deal size " +
      "is where cash-flow fragility surfaces.",

    good_answer_looks_like:
      "A blend that matches actual revenue recognition over the last 12 " +
      "months, not aspiration. A primary revenue type named with " +
      "awareness of what that model's operating rhythm actually demands. " +
      "A typical deal size quoted as a range, not a point estimate — " +
      "reality is always a range. Payment terms honest about what " +
      "customers actually pay, not what the invoice says. Gross margin " +
      "as a range — 'not sure' is better than a made-up number. For " +
      "retainer or subscription businesses, an honest read on contract " +
      "length that includes renewal reality.",

    probe_deeper_on:
      "'We do a mix' without specifics — probe the actual ratios from " +
      "the last 12 months of revenue. Gross margin offered confidently " +
      "at 70%+ without tracking delivery cost properly — probe what's " +
      "included and what isn't. Payment terms stated as 'Net 30' when " +
      "customers routinely pay late — surface that the stated term " +
      "isn't the actual term. 'Our typical deal is £10k' when the " +
      "median is £3k and there's one £50k outlier — probe median vs " +
      "mean. Deal size given as a point estimate — reality is a range, " +
      "ask for it. Subscription claimed when reality is contracted " +
      "retainers — different economics, worth naming correctly.",

    conversation_archetype:
      "Teach the blend frame first: 'Businesses make money in different " +
      "ways, usually a mix. The mix matters more than any single type " +
      "because each has different operating implications — project " +
      "work, retainer, subscription, transactions and commission don't " +
      "just have different revenue shapes; they have different team " +
      "rhythms, pricing dynamics, and growth ceilings.' Extract the " +
      "blend from the last 12 months: 'Roughly what share of revenue " +
      "came from each type — projects with a start and end, retained/" +
      "recurring work, subscription or productised revenue, one-off " +
      "transactions, commissions?' Categorise stories into the five " +
      "types. Propose a starting allocation; founder adjusts sliders. " +
      "Pressure-test: 'Your blend shows 55% project and 35% retainer. " +
      "Which is actually driving the business forward? Which one, if " +
      "you had to cut, would hurt the most?' That forces the " +
      "primary_revenue_type pick. Then capture deal size as a range. " +
      "Payment terms with a probe at what customers actually pay, not " +
      "invoice terms. Gross margin as a range — invite 'not sure' if " +
      "the founder genuinely doesn't know. If the blend includes " +
      "retainer or subscription above ~10%, ask about typical contract " +
      "length. Close with cross-reference probes: price_positioning vs " +
      "typical_deal_size; gross_margin vs price_positioning; " +
      "primary_revenue_type vs market_positioning; CAC (from GTM) vs " +
      "typical_deal_size for a unit-economics payback read.",

    // ── Future outlook (trend annotation + new entries) ─────────────
    future_outlook_instruction:
      "After today's revenue model is captured, run ONE closing pass: " +
      "'Will today's revenue shape hold over your vision horizon? Same " +
      "mix at scale, small shift, or material shift?' Then probe the " +
      "likely shifts — toward retainer, toward subscription, bigger " +
      "deal sizes, smaller deals at higher volume, higher margin, lower " +
      "margin at higher scale. Capture each shift plus one line on " +
      "what forces the change. Then ONE separate question for new " +
      "streams: 'What new revenue streams do you expect to add by " +
      "your vision horizon, and the strategic rationale for each?' " +
      "Don't push if it's hazy; empty is fine. Maps to section-level " +
      "_future block and future_additions list.",

    output_structure: 'grouped',

    option_paradigm:
      "weighted_blend_with_pressure_test — reused from Go to Market. " +
      "Five revenue types on sliders summing to 100%; AI proposes a " +
      "starting allocation from conversation; founder adjusts; pressure " +
      "test forces primary_revenue_type pick. The blend describes " +
      "reality; the primary pick is the strategic anchor for cross-" +
      "reference coherence checks. Reuses the split question widget " +
      "from questions.js.",

    field_notes: {
      business_type:
        "KEEP existing field. Multi-select chip: B2B / B2C / C2C / C2B " +
        "/ D2C. Display label: 'Who we sell to'.",
      revenue_blend:
        "NEW PRIMARY FIELD. Slider allocation summing to 100% across " +
        "five types: Project-based (one-off engagements with a start " +
        "and end); Retainer / recurring service (contracted recurring " +
        "work); Subscription / product (automated recurring product " +
        "revenue); Transaction / one-off purchase (single sales without " +
        "commitment); Commission / share of revenue (% of something " +
        "that flows through — marketplace, affiliate, royalty). AI " +
        "proposes starting allocation from conversation; founder " +
        "adjusts. Reuses split question widget from questions.js.",
      primary_revenue_type:
        "NEW. Single-choice reference to one of the five blend types. " +
        "Display label: 'What's driving the business forward'. Output " +
        "of the pressure-test question. Forcing the pick is the " +
        "strategic anchor for cross-reference coherence checks.",
      typical_deal_size:
        "NEW. Free text, range expected. Display label: 'Typical deal " +
        "size (range)'. '£3k–£8k' is a better answer than '£5k'. A " +
        "single point estimate is almost always misleading.",
      payment_terms:
        "NEW. Single-choice chip. Display label: 'When customers pay'. " +
        "Options: Upfront (paid before delivery begins); On completion " +
        "(paid at end of engagement); Net 30 (30-day invoice terms); " +
        "Net 60+ (slower, common in enterprise); Milestone-based " +
        "(staged across engagement); Subscription recurring (automatic " +
        "recurring billing). Probe during conversation for the actual " +
        "pattern, not just invoice terms — customers routinely paying " +
        "late means Net 30 on the invoice is Net 45–60 in practice.",
      gross_margin:
        "NEW AT CANVAS LEVEL. Previously only a Decision Guru question; " +
        "promoted to canvas field because it's a structural revenue-" +
        "model property, not just a diagnostic. Single-choice chip " +
        "with ranges. Display label: 'Gross margin (rough)'. Options: " +
        "Under 20%; 20–40%; 40–60%; 60–80%; Over 80%; Not sure. 'Not " +
        "sure' is a valid answer and probably more honest than a " +
        "guessed percentage. Probe what's included in the cost base — " +
        "direct delivery only, or including some overhead?",
      contract_length_typical:
        "NEW. Conditional — only shown when revenue_blend has retainer " +
        "or subscription above ~10%. Free text. Display label: " +
        "'Typical contract length'. For retainer: '12 months, auto-" +
        "renew, 3-month notice' is honest; for subscription: 'Monthly " +
        "rolling' or 'Annual, 85% renew' is honest. Renewal reality " +
        "matters more than the stated term.",
      model_type:
        "DEPRECATE. Existing chip list is muddled (mixes revenue " +
        "models like Subscription with business shapes like Franchise " +
        "and positioning strategies like Razor & Blade). Replaced by " +
        "revenue_blend + business_type + primary_revenue_type. Data " +
        "migration: Subscription / Fee-for-service collapse into " +
        "revenue_blend as a heavy weight on the corresponding type; " +
        "Franchise / Manufacturer / Brick-and-click map to " +
        "business_type or drop.",
    },

    narrative_output: {
      audience: 'Investor conversations, operating plan context, and new commercial hire onboarding',
      length_target: '1–2 sentences',
      purpose:
        "A short commercial-model summary. Example shape: 'B2B " +
        "consultancy. 60% retainer, 30% project, 10% productised. " +
        "Typical deal £8–12k. Net 30. Gross margin around 60%. " +
        "Retainer is the anchor; project work is where growth happens.' " +
        "Mechanically composable from the fields but valuable as " +
        "editable prose.",
      editable: true,
    },

    decision_guru_triggers: [
      {
        prompt: 'Should we shift from project-based to retainer?',
        prompt_template:
          "Should we shift more of our revenue from project-based to " +
          "retainer, given our {primary_revenue_type} primary, our " +
          "{vision.target_detail} growth ambition, and typical deal " +
          "size of {typical_deal_size}?",
        lens: 'profit',
        triggering_conditions: {
          any_of: [
            {
              type: 'field_majority',
              field: 'revenue_model.revenue_blend[project_based]',
              value: 'over_50_percent',
              rationale: 'project work dominant — treadmill risk',
            },
            {
              type: 'founder_statement',
              pattern: 'cash flow unpredictability',
              rationale: 'founder names feast/famine pattern',
            },
            {
              type: 'cross_ref_tension',
              from: 'vision',
              to: 'revenue_blend',
              rationale: 'project-heavy at scale targets = operating-rhythm tension',
            },
          ],
        },
        when:
          "Common SME inflection, particularly for consultancies and " +
          "agencies where founders grow tired of the sales treadmill of " +
          "bespoke project work. Updates revenue_blend and typically " +
          "triggers reconsideration of pricing and team shape.",
        updates_field: 'revenue_blend',
      },
      {
        prompt: 'Should we add a recurring revenue stream?',
        prompt_template:
          "Should we add a recurring revenue stream alongside our " +
          "{primary_revenue_type} base, given our {vision.target_type} " +
          "ambition?",
        lens: 'profit',
        triggering_conditions: {
          any_of: [
            {
              type: 'field_absent',
              field: 'revenue_model.revenue_blend[subscription + retainer]',
              value: 'under_10_percent',
              rationale: 'no recurring layer in blend',
            },
            {
              type: 'founder_statement',
              pattern: 'growth ambition plus fatigue on project sales',
              rationale: 'ceiling frustration + non-recurring base',
            },
          ],
        },
        when:
          "Current model is transactional or project-only and founder " +
          "considering a product / subscription / retainer layer. " +
          "Updates revenue_blend by introducing a new type weight and " +
          "typically cascades into a Differentiators journey (recurring " +
          "layer often needs a new capability).",
        updates_field: 'revenue_blend',
      },
    ],

    user_facing_intro: {
      what:
        "How money actually flows from customers to your business — " +
        "the mix of ways you charge, which type is driving the business " +
        "forward, and the unit economics (deal size, payment terms, " +
        "gross margin) that shape your operating cash.",
      why:
        "Revenue shape dictates how the business actually runs. " +
        "Project work creates feast/famine rhythm; retainers are " +
        "stable but growth-ceilinged; subscription compounds but leaks " +
        "through churn. Getting the blend explicit makes hiring, " +
        "pricing, and cash-flow decisions coherent. It's also the " +
        "section where underpricing usually surfaces — gross margin " +
        "quoted honestly against your positioning often tells you " +
        "whether you're leaving money on the table.",
      strong_articulation:
        "A blend that reflects the last 12 months of actual revenue, " +
        "not aspiration. A primary type named with a clear sense of " +
        "what that model's rhythm demands. Deal size as a range, not " +
        "a point estimate. Payment terms honest about what customers " +
        "actually pay. Gross margin as a range — 'not sure' is fine; " +
        "a made-up number isn't.",
    },

    cross_reference: {
      up: [
        {
          section: 'founder_purpose',
          checks: [
            'time_horizon coherent with blend — long horizon supports subscription investment; short horizon favours project/transaction cash',
          ],
        },
        {
          section: 'value_proposition',
          checks: [
            'price_positioning vs typical_deal_size — Premium + low deal size is a tension; Low-cost + premium deal size is a tension',
            'market_positioning vs primary_revenue_type — Tailored typically implies project/retainer; Reliable & efficient typically implies subscription/transaction; Best-in-class varies',
          ],
        },
        {
          section: 'customers',
          checks: [
            'segment typical_margin should roughly match gross_margin at section level — if per-segment margins in Customers diverge from the aggregated gross margin, flag',
          ],
        },
        {
          section: 'go_to_market',
          checks: [
            'CAC from GTM vs typical_deal_size gives a unit-economics payback read',
            'recurring models with high CAC need renewal recovery',
          ],
        },
      ],
      down: [
        {
          section: 'cost_model',
          checks: [
            'gross_margin plus revenue_blend tell the other half of the unit-economics story alongside fixed-cost shape',
            'typical_deal_size × volume must clear the fixed-cost base',
          ],
        },
      ],
    },
  },

  cost_model: {
    objective:
      "To surface where money goes each month — the split between fixed " +
      "and variable costs, the biggest cost categories, the rough " +
      "breakeven, and an honest read on margin volatility. The practical " +
      "side of profitability.",

    why_it_matters:
      "Cost shape dictates operating leverage and cash fragility. High " +
      "fixed / low variable means every extra pound of revenue falls to " +
      "the bottom line — but breakeven is high and a downturn hurts. " +
      "High variable / low fixed is flexible but caps scalability. Most " +
      "SME founders haven't done the honest cost review; hidden costs " +
      "turn up (founder time not costed, tech subscription creep, home " +
      "office allocations, contractor spend that became de facto " +
      "employment). Seeing the cost base clearly often reveals that the " +
      "business runs at a loss once founder time is included — which is " +
      "a live strategic fact, not an accounting quirk. Cost shape is " +
      "also where the most common market_positioning tensions surface: " +
      "Best-in-class with skinny marketing or tech spend is almost " +
      "always underinvesting in the thing that's supposed to win.",

    good_answer_looks_like:
      "A cost breakdown that reflects real monthly spend, not an " +
      "idealised version. Founder time treated honestly — either " +
      "explicitly included in the cost base, or at least named as " +
      "excluded so the breakeven figure has context. A fixed/variable " +
      "read that matches how the business would actually behave in a " +
      "downturn (ask 'if revenue halved next month, what could you stop " +
      "spending?' — that's the variable part). A breakeven number " +
      "quoted as a range, or 'not sure' honestly — a made-up precise " +
      "number is worse than useless.",

    probe_deeper_on:
      "Monthly fixed cost quoted confidently without founder salary — " +
      "probe whether founder time is in the figure. If not, probe the " +
      "implication: 'if we added a market-rate founder salary, what " +
      "would the monthly fixed base become? And would breakeven still " +
      "work?' The exercise is more useful than the number. 'We're " +
      "mostly variable' when most costs are actually fixed (premises, " +
      "tech subscriptions, core team salaries) — probe what " +
      "specifically would flex if revenue dropped. Tech subscription " +
      "creep — few SMEs have a clean view of recurring software spend; " +
      "probe for a number, even a rough one. 'Our costs are stable' — " +
      "probe whether stable means predictable or means 'we haven't " +
      "reviewed them in 18 months'. Best-in-class or Premium " +
      "positioning with low Marketing/Sales or Tech weight in the blend " +
      "— surface the tension: 'you're positioned to win on quality/" +
      "expertise, but the cost base doesn't reflect investment in that'.",

    conversation_archetype:
      "Teach the frame first: 'Costs split two ways — fixed (same each " +
      "month regardless of activity) and variable (move with activity). " +
      "The split matters because it tells you operating leverage, " +
      "breakeven, and how fast you can scale — or how exposed you are " +
      "if revenue drops.' Capture the monthly fixed total first so the " +
      "widget has a target: 'Roughly what's your monthly fixed base — a " +
      "range is fine?' Accept 'not sure'. Then extract the breakdown " +
      "via the SplitSelector widget: 'Where does that go? Here are the " +
      "common categories — salaries, rent/premises, technology, " +
      "marketing & sales, professional services, other. Adjust the " +
      "shares to match roughly what you see, or add your own " +
      "categories. Percentages or actual amounts, whichever feels more " +
      "natural.' AI proposes starting allocation from the conversation; " +
      "founder adjusts via the widget directly. No separate pressure-" +
      "test for largest category — the widget's visual bar shows it, " +
      "and any downstream reasoning that needs 'largest' reads it from " +
      "the splits object. Then the fixed/variable question: 'If revenue " +
      "halved next month, what could you actually stop spending in the " +
      "next 30 days? That's your variable share.' Chip from the answer. " +
      "Breakeven as a range if known (or 'not sure'). The founder time " +
      "probe, asked once: 'Quick check — is your own salary in the " +
      "fixed-cost figure, or is it on top? Either is a real position; " +
      "it just matters that we know which one we're talking about.' " +
      "Surface the implication without demanding a rework. Close with " +
      "cross-reference probes: market_positioning vs cost shape (Best-" +
      "in-class + low Tech/Marketing share in fixed_costs = investment " +
      "tension); risk_tolerance vs fixed/variable (Low risk tolerance + " +
      "Mostly fixed = tension); primary_revenue_type vs cost shape " +
      "(project-dominant with high fixed base = feast/famine risk).",

    // ── Future outlook (trend annotation + new entries) ─────────────
    future_outlook_instruction:
      "After today's cost shape is captured, run ONE closing pass: " +
      "'Will the cost shape hold at the scale you're heading toward? " +
      "Same shape, small shift, or material shift?' Then probe the " +
      "likely shifts — heavier on people, marketing, tech/R&D; more " +
      "fixed or more variable; premises step-up at a particular scale. " +
      "Capture each shift plus one line on what's driving it. Then ONE " +
      "separate question for new categories: 'What cost lines do you " +
      "not have today that you expect at target scale — compliance, " +
      "finance hire, dedicated infrastructure, anything else? When does " +
      "each come in?' Don't push if hazy; empty is fine. Maps to " +
      "section-level _future block and future_additions list.",

    output_structure: 'grouped',
    // Capture order matters within this section: monthly_fixed_costs
    // must be captured BEFORE fixed_costs so the SplitSelector widget
    // has a target total to work against in absolute (£) mode. Other
    // fields can appear in any order after those two.

    option_paradigm:
      "weighted_blend — a single structured input via the existing " +
      "SplitSelector widget already built for the Decision Guru profit " +
      "lens (backend/services/questions.js → fixed_costs_breakdown " +
      "definition; frontend DecisionGuru.js → SplitSelector component " +
      "lines 917–1349). Same component, same categories, same config — " +
      "true code reuse. Differs from the weighted_blend_with_pressure_" +
      "test paradigm used in Go to Market and Revenue Model: for cost " +
      "categories there's no separate pressure-test for 'largest' " +
      "because the widget's visual bar shows it directly, and forcing " +
      "a duplicate pick adds friction without value. Fixed/variable " +
      "ratio is a simple 4-option chip rather than a second slider — " +
      "the question is naturally categorical.",

    field_notes: {
      monthly_fixed_costs:
        "KEEP existing field. Free text, range acceptable. Display " +
        "label: 'Rough monthly fixed costs'. Invite 'unclear' or a " +
        "range — a made-up precise number adds no value. CAPTURED " +
        "FIRST in the section so the fixed_costs widget has a target " +
        "total (via absoluteTotalFrom: 'monthly_fixed_costs').",
      fixed_costs:
        "Primary structured field — replaces the earlier-drafted " +
        "fixed_cost_blend + largest_cost_category pair. Reuses the " +
        "existing SplitSelector widget already built for the Decision " +
        "Guru profit lens (backend/services/questions.js → " +
        "fixed_costs_breakdown definition; frontend DecisionGuru.js → " +
        "SplitSelector component lines 917–1349). Widget config " +
        "identical to DG's fixed_costs_breakdown: type 'split'; " +
        "options salaries/premises/tech/marketing/professional/other; " +
        "allowAbsolute true; absoluteTotalFrom 'monthly_fixed_costs'. " +
        "Emitted value shape: { mode, splits, absoluteValues, " +
        "customOpts }. Display label: 'Where the fixed costs go'. " +
        "Largest category not captured as a separate field — widget's " +
        "visual bar shows it directly; downstream reasoning reads it " +
        "from the splits object.",
      fixed_variable_ratio:
        "KEEP existing but RESTRUCTURE from free text to single-choice " +
        "chip. Display label: 'How the cost base flexes'. Options: " +
        "Mostly fixed (cost base stays nearly the same whether busy " +
        "or quiet); Balanced (a meaningful chunk flexes with activity); " +
        "Mostly variable (costs scale up and down with revenue); Not " +
        "sure. Probe during conversation with the 'if revenue halved " +
        "next month, what could you stop spending?' question — that's " +
        "what 'variable' really means, not what the accountant " +
        "categorises.",
      breakeven_estimate:
        "KEEP existing field. Free text, range acceptable. Display " +
        "label: 'Rough monthly breakeven'. Accept 'not sure'. Probe " +
        "whether founder salary is in or out — it changes breakeven " +
        "materially.",
      margin_volatility:
        "KEEP existing field. Single-choice chip: Stable / Moderate / " +
        "Volatile. Display label: 'Margin stability'. Self-report — " +
        "treat as a founder self-check, not a primary input. Probe if " +
        "volatility named but founder claims mostly-fixed cost base — " +
        "volatility usually comes from variable-cost businesses.",
      founder_time_probe:
        "NOT A FIELD. Captured in the conversation archetype as a " +
        "one-shot probe during monthly fixed costs capture: 'Is your " +
        "own salary in that figure, or on top?' Either answer is " +
        "legitimate; what matters is downstream data (breakeven, " +
        "margin, cross-references) has a consistent basis.",
      _deprecated_from_earlier_draft:
        "fixed_cost_blend and largest_cost_category from the earlier " +
        "draft of this section have been removed — replaced by the " +
        "single fixed_costs field using the SplitSelector widget. " +
        "Neither shipped; this is a spec-time correction.",
    },

    // No narrative_output — structural / operational section per the
    // Template block at the top of the spec. What this section
    // captures is useful for internal reasoning and cross-reference
    // coherence but not content the founder carries externally.

    decision_guru_triggers: [
      {
        prompt: 'Should we shift to a more variable cost structure?',
        prompt_template:
          "Should we shift to a more variable cost structure, given " +
          "our {fixed_variable_ratio} cost base, our " +
          "{primary_revenue_type} revenue model, and our " +
          "{vision.target_detail} ambition?",
        lens: 'profit',
        triggering_conditions: {
          any_of: [
            {
              type: 'field_value',
              field: 'cost_model.fixed_variable_ratio',
              value: 'Mostly fixed',
              rationale: 'fixed-heavy base is the typical trigger condition',
            },
            {
              type: 'founder_statement',
              pattern: 'cash fragility or coming downturn / revenue uncertainty',
              rationale: 'operating-leverage concerns named explicitly',
            },
            {
              type: 'cross_ref_tension',
              from: 'founder_purpose.risk_tolerance',
              to: 'cost_model.fixed_variable_ratio',
              rationale: 'low risk tolerance with mostly-fixed base is a live tension',
            },
          ],
        },
        when:
          "Real SME inflection, typically at points of cash fragility " +
          "or when the founder senses growth uncertainty and wants to " +
          "de-risk operating leverage. The journey considers moving " +
          "from employees to contractors, owned premises to flexible, " +
          "committed tech to usage-based, and the trade-offs. Updates " +
          "fixed_variable_ratio and often cascades into hiring / " +
          "Operating Model decisions.",
        updates_field: 'fixed_variable_ratio',
      },
    ],

    user_facing_intro: {
      what:
        "Where your money goes each month — the split between fixed and " +
        "variable, the biggest categories, and your rough breakeven. " +
        "The practical side of profitability.",
      why:
        "Cost shape tells you how the business behaves — operating " +
        "leverage if revenue grows, exposure if it drops. Most founders " +
        "haven't done the honest review; hidden costs (founder time, " +
        "tech subscription creep, home office) usually turn up, and " +
        "naming them is more useful than getting to a perfect number. " +
        "It's also where positioning tensions surface — Best-in-class " +
        "with a skinny tech or marketing base almost always means " +
        "under-investment in the thing that's supposed to win.",
      strong_articulation:
        "A breakdown that reflects real spend, not an idealised " +
        "version. Founder time treated honestly — either in the figure, " +
        "or explicitly named as excluded. A fixed/variable split that " +
        "tracks what could actually flex if revenue dropped. Breakeven " +
        "as a range, or 'not sure' — a guessed precise number tells " +
        "you less than an honest range.",
    },

    cross_reference: {
      up: [
        {
          section: 'founder_purpose',
          checks: [
            'risk_tolerance coherent with fixed_variable_ratio — Low risk tolerance + Mostly fixed is a real tension',
          ],
        },
        {
          section: 'value_proposition',
          checks: [
            'market_positioning coherent with cost-category weighting (Best-in-class + low Tech/Marketing = under-investment tension)',
            'price_positioning coherent with cost shape (Premium with low Professional services might indicate scope drift)',
          ],
        },
        {
          section: 'revenue_model',
          checks: [
            'gross_margin plus revenue_blend tell the other half of the unit-economics story',
            'typical_deal_size × volume must clear the fixed-cost base',
          ],
        },
        {
          section: 'go_to_market',
          checks: [
            'sales_approach has direct cost implications — In-house sales adds People fixed cost; Founder-led caps scale',
            'CAC from GTM should recover from typical_deal_size and cost base together',
          ],
        },
      ],
      down: [],
    },
  },

  current_challenges: {
    objective:
      "To capture the biggest things the business is currently wrestling " +
      "with — named specifically enough to act on, and sharp enough to " +
      "hand to Decision Guru for resolution when the founder is ready.",

    why_it_matters:
      "Challenges are where aspiration meets reality — the friction " +
      "points that block progress toward vision, erode values in " +
      "practice, or stretch the business beyond what its current shape " +
      "can support. Naming them honestly and specifically is how they " +
      "become addressable. Vague challenges stay stuck; sharp challenges " +
      "become decisions. This section is also the most direct bridge " +
      "from the descriptive canvas to the Decision Guru choice engine — " +
      "each captured challenge is a candidate strategic journey, handed " +
      "over with the full business context pre-loaded.",

    good_answer_looks_like:
      "Three to eight entries — fewer usually means under-surfacing, " +
      "more means noise. Each expressed specifically rather than " +
      "generically ('clients stretching payment terms from 30 to 60+ " +
      "days' beats 'cash flow'). Each with one-sentence concrete " +
      "evidence of what's actually happening. Each tagged by area and " +
      "urgency honestly. Each with one-sentence impact if ignored. At " +
      "least an implicit sense of which two or three are the priorities " +
      "for attention now.",

    probe_deeper_on:
      "'Cash flow' / 'Hiring' / 'Growth' as bare labels — probe what " +
      "specifically. Symptoms presented as challenges without the " +
      "underlying cause ('not enough revenue' is a symptom; the " +
      "challenge is whatever causes it). Generic challenges that apply " +
      "to every SME (competition, inflation, regulation) without " +
      "specificity about this business's version. Missing challenges " +
      "the canvas obviously implies — if the founder claims Best-in-" +
      "class positioning but has skinny tech / marketing spend, 'under-" +
      "investment in the thing that's supposed to win' is probably a " +
      "challenge they haven't named; the AI surfaces it. Fifteen-plus " +
      "challenges listed — probe which are really live and which are " +
      "aspirational complaints. Urgency inflated to 'Critical' across " +
      "multiple entries — probe what makes each genuinely critical vs " +
      "high.",

    conversation_archetype:
      "Open with an invitation: 'What are the biggest things the " +
      "business is wrestling with right now? The ones that, if solved, " +
      "would actually move something that matters. List them as you " +
      "think of them — we'll sharpen each one after.' Let the founder " +
      "list freely. For each candidate, offer two framings — sharper " +
      "(specific, evidenced, time-bounded) and more generic — and let " +
      "the founder pick the one that rings truest. Capture evidence " +
      "('what's the concrete thing happening that makes this a " +
      "challenge?'), urgency, and a one-sentence impact-if-ignored " +
      "alongside each. After the founder's list is captured, the AI " +
      "runs a CANVAS COHERENCE SCAN and proposes two or three " +
      "additional candidate challenges detected from tensions in the " +
      "canvas — NON-INTRUSIVELY, as questions not pronouncements: 'I " +
      "noticed your Vision is for £2m ARR in 3 years, and your current " +
      "primary_revenue_type is project-based with moderate deal size — " +
      "that gap might be a challenge worth naming here, or you might " +
      "already have accounted for it. Want to add it, edit it, or " +
      "leave it?' Surface two or three max; founder accepts, edits, or " +
      "dismisses each. Once the combined list is in place, pressure-" +
      "test the prioritisation: 'If you had to pick just three of " +
      "these to tackle next, which would they be?' Not a forced field, " +
      "a focus move — surfaces whether the urgency tags ring true in " +
      "practice. Close with the DG handoff offer: 'Any of these you " +
      "want to work through with Decision Guru right now? I'll take " +
      "the challenge, evidence, and context straight in.'",

    output_structure: 'per_entry_with_continuity',

    option_paradigm:
      "Per-entry two-framings mirror (sharper vs generic) — same " +
      "pattern as Differentiators and Values. AI listens, offers two " +
      "framings for each candidate challenge, founder picks the one " +
      "that rings truest. Plus the CANVAS COHERENCE SCAN addition: AI " +
      "separately proposes 2–3 candidate challenges derived from " +
      "detected canvas tensions; accepted candidates get the same " +
      "two-framings treatment. No section-level option paradigm.",

    field_notes: {
      challenge:
        "KEEP existing field. Free text. Display label: 'The " +
        "challenge'. The core one-line statement. Founder-authored; " +
        "AI sharpens via the two-framings mirror during conversation.",
      evidence:
        "NEW field. Free text, one sentence. Display label: 'What's " +
        "actually happening'. Concrete evidence that this is a live " +
        "challenge, not a generic complaint. Separates symptoms from " +
        "causes.",
      area:
        "KEEP existing field. Single-choice chip. Display label: " +
        "'Challenge area'. Options: Growth / Cash & Finance / People / " +
        "Operations / Market / Technology / Other. Drives the lens " +
        "derivation for the per-entry DG handoff.",
      urgency:
        "KEEP existing but SLIM to 4 options. Display label: " +
        "'Urgency'. Options: Critical (needs action now); High (within " +
        "90 days); Medium (within 6 months); Unclear. Low dropped — if " +
        "it's low it isn't really a challenge worth tracking. Probe if " +
        "multiple entries tag Critical — realistic urgency " +
        "distributions usually cluster in High/Medium.",
      impact_if_ignored:
        "NEW field. Free text, one sentence. Display label: 'If we " +
        "don't address this'. Makes the challenge's importance legible " +
        "without overclaiming. Helps prioritisation — impact-weighted, " +
        "not just urgency-weighted.",
    },

    entry_count_guidance: {
      soft_cap_min: 3,
      soft_cap_max: 8,
      guidance:
        "Communicated in the conversation as 'fewer means under-" +
        "surfacing, more means noise', not a hard limit. If the founder " +
        "genuinely exceeds, the AI flags and suggests the prioritisation " +
        "DG journey rather than blocking.",
    },

    ai_proposed_sources:
      "When running the coherence scan, the AI looks for: (a) cross-" +
      "reference tensions already in the canvas network (Vision " +
      "ambition vs revenue trajectory; Values vs market_positioning); " +
      "(b) field patterns that typically indicate unsurfaced challenges " +
      "(e.g. high fixed_variable_ratio + Low risk_tolerance + long " +
      "time_horizon → operating-leverage exposure often unnamed); (c) " +
      "missing fields that ought to be populated given other choices " +
      "(e.g. Best-in-class positioning with defensibility = 'time " +
      "advantage only' → differentiator investment probably a " +
      "challenge). Maximum 2–3 proposals surfaced per scan — more is " +
      "noise. Each framed as a question, not a pronouncement.",

    // No narrative_output — structural list-like section.

    decision_guru_triggers: [
      {
        prompt: 'Work this through with Decision Guru',
        prompt_template:
          "{challenge}. Context: {evidence}. Impact if ignored: " +
          "{impact_if_ignored}.",
        per_entry: true,
        lens: {
          from_field: 'area',
          mapping: {
            'Growth': 'market',
            'Market': 'market',
            'Cash & Finance': 'profit',
            'Operations': 'profit',
            'People': 'team',
            'Technology': 'triage',
            'Other': 'triage',
          },
        },
        triggering_conditions: {
          type: 'user_initiated',
          surface: 'per_entry_action',
          rationale: 'founder-chosen handoff, no automatic surfacing',
        },
        when:
          "NEW PATTERN: per-entry trigger. First section in the spec " +
          "to use per-entry DG handoff. Each captured challenge gets a " +
          "direct handoff button in the UI; clicking launches a DG " +
          "journey with challenge + evidence + impact pre-populated " +
          "and the lens derived from the entry's area field. Journey " +
          "may write back to the specific challenge entry without " +
          "modifying others.",
        updates_field: 'entries[i]',
      },
      {
        prompt: 'How should we prioritise these challenges?',
        prompt_template:
          "How should we prioritise these challenges: " +
          "{challenges_summary}?",
        lens: 'triage',
        triggering_conditions: {
          any_of: [
            {
              type: 'field_count',
              field: 'current_challenges.entries',
              comparator: '>= 5',
              rationale: 'list long enough that prioritisation is a real question',
            },
            {
              type: 'founder_statement',
              pattern: 'where should I focus',
              rationale: 'founder explicitly asks for prioritisation help',
            },
          ],
        },
        when:
          "Section-level trigger for when the list is long or the " +
          "founder explicitly asks where to focus. Lens kept as 'triage' " +
          "— challenges span lenses, so a hard pre-selection would be " +
          "wrong here. Triage runs with the full challenges list + " +
          "canvas as context.",
        updates_field: null,
      },
    ],

    user_facing_intro: {
      what:
        "The things your business is currently wrestling with — named " +
        "specifically enough to act on, and sharp enough to hand to " +
        "Decision Guru when you're ready to work through one. Not " +
        "everything that's suboptimal — the ones that actually matter " +
        "and need attention soon.",
      why:
        "Vague challenges stay stuck; sharp challenges become decisions. " +
        "This section is the most direct bridge between the canvas " +
        "(what's true) and Decision Guru (what to do next) — each " +
        "captured challenge is a candidate journey, handed over with " +
        "the full context behind it so you don't start from zero.",
      strong_articulation:
        "Specific rather than generic. Backed by concrete evidence of " +
        "what's actually happening. Honest about urgency. A sense of " +
        "what happens if it's not addressed. Three to eight entries is " +
        "usually about right — fewer means under-surfacing, more means " +
        "noise.",
    },

    cross_reference: {
      up: [],
      coherence_scope: 'per_entry_against_full_canvas',
      coherence_note:
        "Each challenge is checked against the full canvas on save; " +
        "AI surfaces any detected tensions for the founder's " +
        "consideration. No enumerated up-refs — tension detection " +
        "happens at entry content level, not section level.",
      down: [],
    },
  },

};

/**
 * Sector segmentation dimensions
 * ─────────────────────────────────────────────────────────────────
 * For founders operating within a single recognisable sector, these
 * are the typical dimensions along which the customer base tends to
 * naturally divide. The AI offers these during the Customers
 * conversation when the founder struggles to identify distinct
 * segments on their own.
 *
 * Match by the `sector` field on the canvas. Fuzzy — "managed IT
 * services" matches "managed_it"; "legal services" matches
 * "professional_services"; "boutique consultancy" matches
 * "professional_services". Fallback to generic dimensions when no
 * sector match is found.
 *
 * First draft. Expand from real-world tester usage.
 */
const SECTOR_SEGMENTATION_DIMENSIONS = {
  professional_services: {
    match_keywords: ['professional services', 'consulting', 'consultancy', 'legal', 'accounting', 'accountancy', 'advisory', 'law', 'solicitor', 'accountant'],
    dimensions: [
      'Client size (headcount or revenue)',
      'Sector of the client (what industry they operate in)',
      'Recurring / retained work vs one-off projects',
      'Complexity of engagement (standard advisory vs deep specialism)',
    ],
  },

  managed_it: {
    match_keywords: ['managed it', 'msp', 'it services', 'technology services', 'cyber', 'cloud services'],
    dimensions: [
      'Client size (headcount or revenue)',
      'Regulatory environment (regulated sectors vs not)',
      'In-house IT maturity (no in-house team / some / full)',
      'Service mix (infrastructure, support, strategic advisory)',
    ],
  },

  creative_agency: {
    match_keywords: ['creative agency', 'design agency', 'branding', 'marketing agency', 'advertising agency', 'digital agency'],
    dimensions: [
      'Client budget tier (entry / mid / premium)',
      'Project type (brand, digital, campaign, retainer)',
      'Sector vertical of the client',
      'Repeat relationship vs one-off brief',
    ],
  },

  recruitment: {
    match_keywords: ['recruitment', 'talent', 'staffing', 'headhunter', 'executive search'],
    dimensions: [
      'Client sector (which industries they recruit for)',
      'Role seniority (entry / mid / senior / exec)',
      'Contingent vs retained engagement',
      'Permanent vs contract placements',
    ],
  },

  trades_construction: {
    match_keywords: ['construction', 'building', 'trades', 'contractor', 'electrical', 'plumbing', 'joinery'],
    dimensions: [
      'Project size (small domestic / mid / major commercial)',
      'Client type (residential / commercial / public sector)',
      'Geographic reach (local / regional / national)',
      'Repeat client vs one-off',
    ],
  },

  _generic: {
    match_keywords: [],
    dimensions: [
      'Customer size (individuals / small businesses / larger organisations)',
      'What they buy (core product/service vs add-ons vs advisory)',
      'How they buy (direct / via partner / self-serve)',
      'Frequency (one-off vs recurring vs continuous relationship)',
    ],
  },
};

/**
 * Helper: look up sector dimensions by matching against a sector string.
 * Returns the dimensions list plus the matched key for logging.
 */
function getSectorDimensions(sectorString) {
  if (!sectorString) return { dimensions: SECTOR_SEGMENTATION_DIMENSIONS._generic.dimensions, matched: '_generic' };
  const needle = String(sectorString).toLowerCase();
  for (const [key, def] of Object.entries(SECTOR_SEGMENTATION_DIMENSIONS)) {
    if (key === '_generic') continue;
    if (def.match_keywords.some(kw => needle.includes(kw))) {
      return { dimensions: def.dimensions, matched: key };
    }
  }
  return { dimensions: SECTOR_SEGMENTATION_DIMENSIONS._generic.dimensions, matched: '_generic' };
}

module.exports = { SECTION_GUIDANCE, SECTOR_SEGMENTATION_DIMENSIONS, getSectorDimensions };
